/* eslint-disable */
import { supabase } from './supabase';
/**
 * ArtistryConnect-Merged.jsx
 * Artizan Ecosystem — Main React Codebase
 * Covers: Artizan Professional Network | The Studio | The Craft Line Engine
 * Founder: Jerone Arnold Darden | @firekeeper_jeada
 * Version: 3.0 | March 2026
 *
 * SETUP:
 *   npm install react react-dom lucide-react
 *   Import this file as your root component.
 *   Requires: Google Fonts (Cormorant Garamond, DM Sans, DM Mono)
 *
 * BACKEND INTEGRATIONS (not yet wired — see Developer Handoff PDF):
 *   - supabase.auth  (replace all authStub calls)
 *   - Stripe         (replace all paymentStub calls)
 *   - Socket.IO      (replace all realtimeStub calls)
 *   - NCMEC PhotoDNA (wire to uploadFile utility)
 */

import { useState, useReducer, useEffect, useRef } from 'react';
import { getPosts, getJobs, getConnections, sendConnectionRequest, updateConnectionStatus, getMessages, sendMessage, getThreads, getVerificationSignals, upsertVerificationSignals, getAllProfiles, uploadAvatar, uploadMedia, getCastings, postCasting, applyCasting, getCastingApplications, shortlistApplicant, getSuggestedConnections, getProfileStats, createPost, getNotifications, markNotificationsRead, createNotification, recordProfileView, followUser, unfollowUser, getFollowStatus, likePost, unlikePost, addComment, getComments, applyToJob, getJobApplicants, globalSearch } from './db';
import AvatarCrop from './AvatarCrop';
import AisleView from './AisleView';
import VizionView from './VizionView';
import CurtainzView from './CurtainzView';
import { MobileTabBar, CreateSheet, MoreSheet, FilterSheet, SkeletonCard } from './MobileLayout';
import AuditionTracker from './AuditionTracker';
import ContractVault from './ContractVault';
import RosterManager from './RosterManager';
import SelfTapeStudio from './SelfTapeStudio';
import CraftCommons from './CraftCommons';
import BrandDealTracker from './BrandDealTracker';
import FeatureTour, { FEATURES, ProgressCard } from './FeatureTour';
import { getFeatureProgress, markFeatureExplored, markReminderShown } from './db';
import Sparky from './Sparky';
import {
  Home, Search, Briefcase, Users, MessageCircle, BarChart2, Settings,
  Bell, Star, Shield, Play, Upload, Heart, Share2, Bookmark, MoreHorizontal,
  MapPin, Clock, ChevronRight, ChevronDown, ChevronUp, Plus, X, Check,
  Filter, Send,
  Music, Video, Zap,
  DollarSign,
  ArrowLeft, Edit,
  Menu, Image, Layers, Compass, Clapperboard, ClipboardList, FileText} from 'lucide-react';

// ─────────────────────────────────────────────
// MOBILE DETECTION HOOK
// ─────────────────────────────────────────────
function useWindowWidth() {
  const [width, setWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1024);
  useEffect(() => {
    const handler = () => setWidth(window.innerWidth);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return width;
}


// ─────────────────────────────────────────────
// DESIGN TOKENS
// ─────────────────────────────────────────────
const T = {
  bg:      '#06060d',
  s1:      '#0d0c18',
  s2:      '#12111e',
  s3:      '#181628',
  s4:      '#1e1c30',
  border:  'rgba(255,255,255,0.07)',
  border2: 'rgba(255,255,255,0.13)',
  gold:    '#d4a853',
  gold2:   '#f0c878',
  amber:   '#b8843a',
  cream:   '#f0ead8',
  text:    '#ede8f5',
  text2:   '#9890b0',
  text3:   '#55506a',
  success: '#3ecf8e',
  error:   '#ff6b6b',
  warn:    '#f0c058',
  purple:  '#8b5cf6',
  teal:    '#1a8a76',
};

const G = {
  gold:   `linear-gradient(135deg, ${T.gold}, ${T.gold2})`,
  dark:   `linear-gradient(135deg, ${T.s2}, ${T.s4})`,
  purple: `linear-gradient(135deg, #8b5cf6, #d4a853)`,
};

// ─────────────────────────────────────────────
// CRAFT LINE ENGINE — Proprietary Algorithm
// Weighted 6-dimension composite score (0–100)
// Coefficients: craft 0.30, network 0.20,
//   activity 0.18, collab 0.14, endorsement 0.10,
//   completeness 0.08
// ─────────────────────────────────────────────
const CRAFT_LINE_WEIGHTS = {
  craftQuality:        0.30,
  networkDepth:        0.20,
  activityConsistency: 0.18,
  collaborationHistory:0.14,
  endorsementQuality:  0.10,
  profileCompleteness: 0.08,
};

function sanitizeUrl(url) {
  if (!url) return '';
  try {
    const u = new URL(url);
    if (u.protocol === 'http:' || u.protocol === 'https:') return url;
    return '';
  } catch { return ''; }
}

function computeCraftLineScore(dimensions) {
  const {
    craftQuality = 0,
    networkDepth = 0,
    activityConsistency = 0,
    collaborationHistory = 0,
    endorsementQuality = 0,
    profileCompleteness = 0,
  } = dimensions;

  const raw =
    craftQuality        * CRAFT_LINE_WEIGHTS.craftQuality +
    networkDepth        * CRAFT_LINE_WEIGHTS.networkDepth +
    activityConsistency * CRAFT_LINE_WEIGHTS.activityConsistency +
    collaborationHistory* CRAFT_LINE_WEIGHTS.collaborationHistory +
    endorsementQuality  * CRAFT_LINE_WEIGHTS.endorsementQuality +
    profileCompleteness * CRAFT_LINE_WEIGHTS.profileCompleteness;

  return Math.min(100, Math.round(raw));
}

// ─────────────────────────────────────────────
// VERIFICATION SCORE SYSTEM
// Max possible raw: 102 → normalized to 100
// ─────────────────────────────────────────────
const VERIFICATION_POINTS = {
  email:           5,   // once
  phone:           5,   // once
  socialProofLink: 8,   // per link, max 3 (24 pts max)
  portfolioUpload: 10,  // once
  credentialUpload:12,  // once
  peerCoSignature: 8,   // per peer, max 3 (24 pts max)
  venueCoSignature:15,  // once
  industryPro:     13,  // once
};

function computeVerificationScore(signals) {
  const {
    email = false,
    phone = false,
    socialProofLinks = 0,
    portfolioUpload = false,
    credentialUpload = false,
    peerCoSignatures = 0,
    venueCoSignature = false,
    industryProEndorsement = false,
  } = signals;

  const raw =
    (email ? VERIFICATION_POINTS.email : 0) +
    (phone ? VERIFICATION_POINTS.phone : 0) +
    Math.min(socialProofLinks, 3) * VERIFICATION_POINTS.socialProofLink +
    (portfolioUpload ? VERIFICATION_POINTS.portfolioUpload : 0) +
    (credentialUpload ? VERIFICATION_POINTS.credentialUpload : 0) +
    Math.min(peerCoSignatures, 3) * VERIFICATION_POINTS.peerCoSignature +
    (venueCoSignature ? VERIFICATION_POINTS.venueCoSignature : 0) +
    (industryProEndorsement ? VERIFICATION_POINTS.industryPro : 0);

  return Math.min(100, Math.round((raw / 102) * 100));
}

// Gate thresholds
const VERIFICATION_GATES = { tier1: 25, tier2: 55, tier3: 80 };

function getVerificationGate(score) {
  if (score >= VERIFICATION_GATES.tier3) return 3;
  if (score >= VERIFICATION_GATES.tier2) return 2;
  if (score >= VERIFICATION_GATES.tier1) return 1;
  return 0;
}

// ─────────────────────────────────────────────
// MOCK DATA
// ─────────────────────────────────────────────
const MOCK_CURRENT_USER = {
  id: null,
  name: 'Your Name',
  handle: '@handle',
  profileType: 'artisan',
  crafts: [],
  location: '',
  avatar: '👤',
  bio: '',
  verificationSignals: {
    email: false, phone: false, socialProofLinks: 0,
    portfolioUpload: false, credentialUpload: false,
    peerCoSignatures: 0, venueCoSignature: false,
    industryProEndorsement: false,
  },
  craftLineDimensions: {
    craftQuality: 0, networkDepth: 0, activityConsistency: 0,
    collaborationHistory: 0, endorsementQuality: 0, profileCompleteness: 0,
  },
  subscription: 'free',
  isIndustryPro: false,
  followers: 0, following: 0, connections: 0,
  joinedAt: null,
};


// ─────────────────────────────────────────────
// STATE MANAGEMENT
// ─────────────────────────────────────────────
const initialState = {
  view: 'feed',
  theme: 'dark',
  user: MOCK_CURRENT_USER,
  posts: [],
  likedPosts: new Set(),
  savedPosts: new Set(),
  notifications: [
    { id: 'n1', type: 'connection', text: 'Dean Oluwafemi wants to connect', time: '5m', read: false },
    { id: 'n2', type: 'cast', text: 'You were shortlisted for "Meridian" DP role', time: '1h', read: false },
    { id: 'n3', type: 'verification', text: 'Your portfolio upload added 10 verification points', time: '2h', read: true },
  ],
  notifSettings: {
    connectionRequests: true, castingShortlists: true, jobMatches: true,
    messageReceipts: false, verificationUpdates: true, craftLineChanges: false,
    platformAnnouncements: true, emailDigest: true, pushEnabled: true,
  },
  activeThread: null,
  chatMessages: {
    m1: [
      { id: 'cm1', from: 'Ava Chen', text: 'Are you available for the shoot next Saturday?', time: '10m', me: false },
      { id: 'cm2', from: 'me', text: 'Let me check my calendar. What time?', time: '8m', me: true },
      { id: 'cm3', from: 'Ava Chen', text: '10am start, should wrap by 4. Outdoor location in Bethlehem.', time: '6m', me: false },
    ],
    m2: [
      { id: 'cm4', from: 'Marcus Webb', text: 'Let me know what you think of the reel.', time: '2h', me: false },
      { id: 'cm5', from: 'me', text: 'Just watched it — the opening sequence is incredible.', time: '1h', me: true },
    ],
    m3: [
      { id: 'cm6', from: 'Priya Nair', text: 'I can have the costume ready by Friday.', time: '1d', me: false },
    ],
  },
  selectedJob: null,
  selectedTalent: null,
  searchQuery: '',
  castingFilters: { type: 'all', location: 'all', rate: 'all' },
  sidebarOpen: true,
  toasts: [],
};

function reducer(state, action) {
  switch (action.type) {
    case 'SET_USER':
      return { ...state, user: { ...state.user, ...action.payload } };
    case 'SET_VIEW':
      return { ...state, view: action.payload, selectedJob: null, selectedTalent: null };
    case 'TOGGLE_THEME':
      return { ...state, theme: state.theme === 'dark' ? 'light' : 'dark' };
    case 'TOGGLE_LIKE':
      const liked = new Set(state.likedPosts);
      liked.has(action.payload) ? liked.delete(action.payload) : liked.add(action.payload);
      return { ...state, likedPosts: liked };
    case 'TOGGLE_SAVE':
      const saved = new Set(state.savedPosts);
      saved.has(action.payload) ? saved.delete(action.payload) : saved.add(action.payload);
      return { ...state, savedPosts: saved };
    case 'SET_THREAD':
      return { ...state, activeThread: action.payload };
    case 'SEND_MESSAGE':
      const { threadId, text } = action.payload;
      const newMsg = { id: `cm_${Date.now()}`, from: 'me', text, time: 'now', me: true };
      return {
        ...state,
        chatMessages: {
          ...state.chatMessages,
          [threadId]: [...(state.chatMessages[threadId] || []), newMsg],
        },
      };
    case 'SELECT_JOB':
      return { ...state, selectedJob: action.payload, view: 'jobs' };
    case 'SELECT_TALENT':
      return { ...state, selectedTalent: action.payload, view: 'talent' };
    case 'SET_SEARCH':
      return { ...state, searchQuery: action.payload };
    case 'TOGGLE_NOTIF_SETTING':
      return { ...state, notifSettings: { ...state.notifSettings, [action.payload]: !state.notifSettings[action.payload] } };
    case 'MARK_NOTIFS_READ':
      return { ...state, notifications: state.notifications.map(n => ({ ...n, read: true })) };
    case 'ADD_TOAST':
      return { ...state, toasts: [...state.toasts, { id: Date.now(), ...action.payload }] };
    case 'REMOVE_TOAST':
      return { ...state, toasts: state.toasts.filter(t => t.id !== action.payload) };
    case 'TOGGLE_SIDEBAR':
      return { ...state, sidebarOpen: !state.sidebarOpen };
    default:
      return state;
  }
}

// ─────────────────────────────────────────────
// AUTH STUBS — Replace with supabase.auth
// ─────────────────────────────────────────────
const authStub = {
  signIn: async (email, password) => {
    // TODO: const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    return new Promise(resolve => setTimeout(() => resolve({ user: MOCK_CURRENT_USER }), 800));
  },
  signUp: async (email, password, metadata) => {
    // TODO: const { data, error } = await supabase.auth.signUp({ email, password, options: { data: metadata } });
    return new Promise(resolve => setTimeout(() => resolve({ user: MOCK_CURRENT_USER }), 1200));
  },
  signOut: async () => {
    // TODO: await supabase.auth.signOut();
    return new Promise(resolve => setTimeout(resolve, 400));
  },
  getSession: async () => {
    // TODO: const { data: { session } } = await supabase.auth.getSession();
    return new Promise(resolve => setTimeout(() => resolve({ session: { user: MOCK_CURRENT_USER } }), 200));
  },
};

// ─────────────────────────────────────────────
// PAYMENT STUBS — Replace with Stripe
// ─────────────────────────────────────────────
const paymentStub = {
  createSubscription: async (priceId) => {
    // TODO: Call your backend to create Stripe subscription
    // Backend: stripe.subscriptions.create({ customer, items: [{ price: priceId }] })
    return new Promise(resolve => setTimeout(() => resolve({ subscriptionId: 'sub_stub', status: 'active' }), 1000));
  },
  createPaymentIntent: async (amount, currency = 'usd') => {
    // TODO: Backend: stripe.paymentIntents.create({ amount, currency, automatic_payment_methods: { enabled: true } })
    return new Promise(resolve => setTimeout(() => resolve({ clientSecret: 'pi_stub_secret' }), 800));
  },
  // Live event ticket fee: platform takes 10%, artisan keeps 90%
  calculatePlatformFee: (ticketPrice) => Math.round(ticketPrice * 0.10),
  calculateArtisanShare: (ticketPrice) => Math.round(ticketPrice * 0.90),
};

// ─────────────────────────────────────────────
// REALTIME STUBS — Replace with Socket.IO
// ─────────────────────────────────────────────
const realtimeStub = {
  connect: () => {
    // TODO: const socket = io(process.env.REACT_APP_SOCKET_URL);

  },
  onEmberExpiry: (postId, callback) => {
    // TODO: socket.on('ember:expired', ({ postId }) => callback(postId));
  },
  onNewMessage: (threadId, callback) => {
    // TODO: socket.on(`message:${threadId}`, callback);
  },
  onPresence: (boardId, callback) => {
    // TODO: Supabase Realtime presence channel for Vizion boards
    // supabase.channel(`board:${boardId}`).on('presence', { event: 'sync' }, callback).subscribe();
  },
};

// ─────────────────────────────────────────────
// FILE UPLOAD STUB — Wire NCMEC PhotoDNA before enabling
// ─────────────────────────────────────────────
const PHOTODNA_CONFIG = {
  ENABLED: false,
  API_KEY: 'REPLACE_WITH_NCMEC_KEY',
  ENDPOINT: 'https://api.microsoftphotodna.com/matchimage',
};

const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'video/mp4', 'audio/mpeg', 'application/pdf'];
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_SIZE_MB = 50;

async function scanWithPhotoDNA(file) {
  if (!PHOTODNA_CONFIG.ENABLED) {
    console.warn('[PhotoDNA] Scanning disabled — NCMEC account pending. File not scanned:', file.name);
    return { clean: true, skipped: true };
  }
  try {
    const formData = new FormData();
    formData.append('file', file);
    const response = await fetch(PHOTODNA_CONFIG.ENDPOINT, {
      method: 'POST',
      headers: { 'Ocp-Apim-Subscription-Key': PHOTODNA_CONFIG.API_KEY },
      body: formData,
    });
    if (!response.ok) throw new Error('PhotoDNA API error: ' + response.status);
    const result = await response.json();
    if (result?.IsMatch === true) {
      console.error('[PhotoDNA] CSAM match detected — upload blocked:', file.name);
      return { clean: false, skipped: false, reason: 'File flagged by PhotoDNA scan' };
    }
    return { clean: true, skipped: false };
  } catch (err) {
    console.error('[PhotoDNA] Scan error:', err);
    if (PHOTODNA_CONFIG.ENABLED) return { clean: false, skipped: false, reason: 'Upload blocked — scan failed' };
    return { clean: true, skipped: true };
  }
}

const uploadStub = {
  validateFile: (file) => {
    if (!ALLOWED_TYPES.includes(file.type)) return { valid: false, error: 'File type not allowed. Accepted: JPEG, PNG, WebP, MP4, MP3, PDF.' };
    if (file.size > MAX_SIZE_MB * 1024 * 1024) return { valid: false, error: `File too large. Maximum size is ${MAX_SIZE_MB}MB.` };
    return { valid: true };
  },
  uploadFile: async (file, bucket) => {
    const validation = uploadStub.validateFile(file);
    if (!validation.valid) return { url: null, path: null, error: validation.error };
    if (ALLOWED_IMAGE_TYPES.includes(file.type)) {
      const scan = await scanWithPhotoDNA(file);
      if (!scan.clean) return { url: null, path: null, error: scan.reason || 'Upload blocked by content scan' };
    }
    return new Promise(resolve =>
      setTimeout(() => resolve({
        url: URL.createObjectURL(file),
        path: `${bucket}/${Date.now()}_${file.name}`,
        error: null,
      }), 1000)
    );
  },
};

// ─────────────────────────────────────────────
// SHARED STYLES
// ─────────────────────────────────────────────
const S = {
  card: {
    background: T.s2,
    border: `1px solid ${T.border}`,
    borderRadius: 12,
    padding: '1.25rem',
  },
  btn: {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    padding: '0.5rem 1rem', borderRadius: 8, fontSize: 13,
    fontWeight: 600, cursor: 'pointer', border: 'none',
    fontFamily: 'inherit', transition: 'all 0.15s ease',
  },
  btnGold: {
    background: G.gold, color: T.bg,
  },
  btnGhost: {
    background: 'transparent', color: T.text2,
    border: `1px solid ${T.border2}`,
  },
  input: {
    background: T.s3, border: `1px solid ${T.border2}`,
    borderRadius: 8, padding: '0.6rem 0.9rem',
    color: T.text, fontSize: 14, width: '100%',
    outline: 'none', fontFamily: 'inherit',
  },
  badge: {
    display: 'inline-flex', alignItems: 'center', gap: 4,
    padding: '2px 8px', borderRadius: 20, fontSize: 11,
    fontWeight: 600,
  },
  avatar: {
    width: 40, height: 40, borderRadius: '50%',
    background: T.s4, display: 'flex',
    alignItems: 'center', justifyContent: 'center',
    fontSize: 18, border: `1px solid ${T.border2}`,
    flexShrink: 0,
  },
};

// ─────────────────────────────────────────────
// UTILITY COMPONENTS
// ─────────────────────────────────────────────
function Avatar({ emoji, size = 40, border = false }) {
  return (
    <div style={{
      ...S.avatar, width: size, height: size, fontSize: size * 0.45,
      border: border ? `2px solid ${T.gold}` : `1px solid ${T.border2}`,
    }}>
      {emoji}
    </div>
  );
}

function Badge({ children, color = T.gold, bg, icon }) {
  return (
    <span style={{ ...S.badge, color, background: bg || `${color}18` }}>
      {icon && <span style={{ display: 'flex' }}>{icon}</span>}
      {children}
    </span>
  );
}

function VerificationBadge({ score }) {
  const gate = getVerificationGate(score);
  const config = {
    0: { label: 'Unverified', color: T.text3 },
    1: { label: 'Emerging', color: T.warn },
    2: { label: 'Verified', color: T.teal },
    3: { label: 'Fully Verified', color: T.gold },
  };
  const { label, color } = config[gate];
  return (
    <span style={{ ...S.badge, color, background: `${color}18`, gap: 4 }}>
      <Shield size={10} strokeWidth={2.5} />
      {label}
    </span>
  );
}

function CraftLineScorePill({ score }) {
  const color = score >= 80 ? T.gold : score >= 60 ? T.teal : T.text2;
  return (
    <span style={{ ...S.badge, color, background: `${color}15`, fontFamily: 'DM Mono, monospace' }}>
      <Zap size={10} />
      CL {score}
    </span>
  );
}

function ScoreBar({ value, max = 100, color = T.gold, height = 4 }) {
  return (
    <div style={{ background: T.s4, borderRadius: height, height, overflow: 'hidden' }}>
      <div style={{
        width: `${(value / max) * 100}%`, height: '100%',
        background: color, borderRadius: height,
        transition: 'width 0.6s ease',
      }} />
    </div>
  );
}

function Toast({ toast, onRemove }) {
  useEffect(() => {
    const t = setTimeout(() => onRemove(toast.id), 3000);
    return () => clearTimeout(t);
  }, [toast.id, onRemove]);

  const colors = { success: T.success, error: T.error, info: T.gold, warn: T.warn };
  const color = colors[toast.type] || T.gold;

  return (
    <div style={{
      background: T.s3, border: `1px solid ${color}40`,
      borderLeft: `3px solid ${color}`,
      borderRadius: 8, padding: '0.75rem 1rem',
      display: 'flex', alignItems: 'center', gap: 10,
      marginBottom: 8, fontSize: 13, color: T.text,
      boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
      animation: 'slideIn 0.2s ease',
    }}>
      <span style={{ color }}>{toast.icon || '✦'}</span>
      {toast.message}
    </div>
  );
}

// ─────────────────────────────────────────────
// VIEWS
// ─────────────────────────────────────────────

// ── FEED VIEW ──
const TOUR_MOCK_POSTS = [
  { id:'m1', user_id:'tour', profiles:{handle:'@ava.lens',display_name:'Ava Chen',crafts:['Photographer'],avatar_url:null}, content:'New editorial series dropping next week. Three days on location in the Poconos. The light was something else.', like_count:48, comment_count:12, created_at:new Date().toISOString() },
  { id:'m2', user_id:'tour', profiles:{handle:'@marcus.directs',display_name:'Marcus Webb',crafts:['Director','Producer'],avatar_url:null}, content:'Vizion board for the next short is almost locked. Looking for a DP with strong naturalistic lighting experience.', like_count:31, comment_count:24, created_at:new Date().toISOString() },
  { id:'m3', user_id:'tour', profiles:{handle:'@priya.costume',display_name:'Priya Nair',crafts:['Costume Designer'],avatar_url:null}, content:'Three looks I built for NEPA FW this season. All sourced, constructed, or upcycled within a 60-mile radius.', like_count:87, comment_count:19, created_at:new Date().toISOString() },
];

function FeedView({ state, dispatch, realPosts, loadRealPosts, tourMode }) {
  const effectivePosts = tourMode && (!realPosts || realPosts.length === 0) ? TOUR_MOCK_POSTS : (realPosts || []);
  const windowWidth = useWindowWidth();
  const isMobile = windowWidth < 768;
  const { likedPosts, savedPosts } = state;
  const [openComments, setOpenComments] = useState(null);
  const [comments, setComments] = useState([]);
  const [commentInput, setCommentInput] = useState('');
  const [loadingComments, setLoadingComments] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);
  const [deletingPost, setDeletingPost] = useState(null);

  const handleOpenComments = async (postId) => {
    setOpenComments(postId);
    setLoadingComments(true);
    const data = await getComments(postId);
    setComments(data);
    setLoadingComments(false);
  };

  const handleAddComment = async () => {
    if (!commentInput.trim() || !userId || !openComments) return;
    const result = await addComment(openComments, userId, commentInput);
    if (result) {
      setComments(prev => [...prev, result]);
      setCommentInput('');
      if (loadRealPosts) await loadRealPosts();
    }
  };
  const posts = realPosts || [];
  const [newPost, setNewPost] = useState('');
  const [composing, setComposing] = useState(false);
  const [mediaFiles, setMediaFiles] = useState([]);
  const [uploadingMedia, setUploadingMedia] = useState(false);

  const handleMediaSelect = (e) => {
    const files = Array.from(e.target.files);
    setMediaFiles(prev => [...prev, ...files].slice(0, 4));
  };

  const removeMedia = (idx) => setMediaFiles(prev => prev.filter((_,i) => i !== idx));

  const userId = state.user?.id;
  const handleLike = async (postId) => {
    const isLiked = likedPosts.has(postId);
    dispatch({ type: 'TOGGLE_LIKE', payload: postId });
    if (userId) {
      if (isLiked) await unlikePost(postId, userId);
      else await likePost(postId, userId);
    }
    dispatch({ type: 'ADD_TOAST', payload: { type: 'info', message: isLiked ? 'Like removed' : 'Post liked ♡', icon: '♡' } });
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm('Delete this post?')) return;
    setDeletingPost(postId);
    const { deletePost } = await import('./db');
    await deletePost(postId);
    if (loadRealPosts) await loadRealPosts();
    setDeletingPost(null);
    setOpenMenu(null);
  };
  const handlePost = async () => {
    if (!newPost.trim() && mediaFiles.length === 0) return;
    setUploadingMedia(true);
    try {
      let mediaUrls = [];
      for (const file of mediaFiles) {
        const url = await uploadMedia(state.user.id, file, 'posts');
        if (url) mediaUrls.push(url);
      }
      const userId = state.user && state.user.id;
      if (userId) {
        const created = await createPost(userId, newPost, mediaUrls);
        if (created && loadRealPosts) await loadRealPosts();
      }
      dispatch({ type: 'ADD_TOAST', payload: { type: 'success', message: 'Post shared to The Reel', icon: '✦' } });
      setNewPost('');
      setMediaFiles([]);
      setComposing(false);
    } catch(e) {
      dispatch({ type: 'ADD_TOAST', payload: { type: 'error', message: 'Failed to post', icon: '✕' } });
    }
    setUploadingMedia(false);
  };

  return (<>
    <div className="feed-container" style={{ maxWidth: 680, margin: '0 auto' }}>
      {/* Compose */}
      <div style={{ ...S.card, marginBottom: 20 }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
          <Avatar emoji={state.user.avatar} border />
          <div style={{ flex: 1 }}>
            <textarea
              placeholder="What are you working on?"
              value={newPost}
              onFocus={() => setComposing(true)}
              onChange={e => setNewPost(e.target.value)}
              style={{
                ...S.input, resize: 'none', minHeight: composing ? 100 : 44,
                transition: 'min-height 0.2s ease',
              }}
            />
            {composing && mediaFiles.length > 0 && (
              <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginTop:8 }}>
                {mediaFiles.map((file, i) => (
                  <div key={i} style={{ position:'relative', width:72, height:72, borderRadius:8, overflow:'hidden', border:`1px solid ${T.border2}` }}>
                    <img src={URL.createObjectURL(file)} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }} />
                    <button onClick={() => removeMedia(i)} style={{ position:'absolute', top:2, right:2, width:18, height:18, borderRadius:'50%', background:'rgba(0,0,0,0.7)', border:'none', color:'white', fontSize:10, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}>✕</button>
                  </div>
                ))}
              </div>
            )}
            {composing && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10 }}>
                <div style={{ display: 'flex', gap: 8 }}>
                  <label style={{ ...S.btn, ...S.btnGhost, padding: '6px 10px', cursor:'pointer' }}>
                    <Image size={15} color={T.text2} />
                    <input type="file" accept="image/*,video/*" multiple onChange={handleMediaSelect} style={{ display:'none' }} />
                  </label>
                  {mediaFiles.length > 0 && (
                    <span style={{ fontSize:11, color:T.text2, alignSelf:'center' }}>{mediaFiles.length} file{mediaFiles.length>1?'s':''} selected</span>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button style={{ ...S.btn, ...S.btnGhost }} onClick={() => { setComposing(false); setNewPost(''); setMediaFiles([]); }}>Cancel</button>
                  <button style={{ ...S.btn, ...S.btnGold }} onClick={handlePost} disabled={uploadingMedia}>
                    <Send size={14} /> {uploadingMedia ? 'Uploading...' : 'Share to Reel'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Posts */}
      {posts.length === 0 && (
        <div style={{ textAlign: 'center', padding: '4rem 2rem', color: T.text3 }}>
          <div style={{ fontSize: 40, marginBottom: 16 }}>✦</div>
          <div style={{ fontSize: 16, fontWeight: 600, color: T.text2, marginBottom: 8 }}>The Reel is empty</div>
          <div style={{ fontSize: 13, lineHeight: 1.6 }}>Be the first to post. Share your work, a project, or what you're building.</div>
        </div>
      )}
      {posts.map(post => (
        <div key={post.id} style={{ ...S.card, marginBottom: 16 }}>
          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              {post.profiles?.avatar_url ? <img src={post.profiles.avatar_url} alt="avatar" style={{width:38,height:38,borderRadius:'50%',objectFit:'cover',border:`2px solid ${T.border2}`}} /> : <Avatar emoji={post.avatar} />}
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontWeight: 600, fontSize: 14 }}>{post.userName}</span>
                  {post.verified && <Shield size={13} color={T.gold} fill={T.gold} />}
                  <CraftLineScorePill score={post.craftLineScore} />
                </div>
                <div style={{ fontSize: 12, color: T.text2 }}>
                  {post.userHandle || post.profiles?.handle} · <span style={{overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',maxWidth:isMobile?'180px':'none',display:'inline-block',verticalAlign:'bottom'}}>{(post.crafts || post.profiles?.crafts || []).slice(0,3).join(', ')}{(post.crafts || post.profiles?.crafts || []).length > 3 ? '...' : ''}</span> · {post.timestamp || new Date(post.created_at).toLocaleDateString()}
                </div>
              </div>
            </div>
            <div style={{ position:'relative' }}>
              <button onClick={() => setOpenMenu(openMenu === post.id ? null : post.id)} style={{ ...S.btn, ...S.btnGhost, padding: '6px 8px' }}>
                <MoreHorizontal size={16} color={T.text2} />
              </button>
              {openMenu === post.id && (
                <div style={{ position:'absolute', right:0, top:'100%', background:T.s2, border:`1px solid ${T.border2}`, borderRadius:8, zIndex:100, minWidth:120, boxShadow:'0 8px 24px rgba(0,0,0,0.4)' }}>
                  {(post.userId === state.user?.id || post.user_id === state.user?.id) && (
                    <button onClick={() => handleDeletePost(post.id)} disabled={deletingPost === post.id} style={{ ...S.btn, width:'100%', justifyContent:'flex-start', padding:'0.6rem 1rem', fontSize:13, color:T.error, background:'none', borderRadius:8 }}>
                      {deletingPost === post.id ? 'Deleting...' : '🗑 Delete Post'}
                    </button>
                  )}
                  <button onClick={() => setOpenMenu(null)} style={{ ...S.btn, width:'100%', justifyContent:'flex-start', padding:'0.6rem 1rem', fontSize:13, color:T.text2, background:'none', borderRadius:8 }}>
                    Cancel
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Ember TTL badge */}
          {post.emberTTL && (
            <div style={{ ...S.badge, color: T.warn, background: `${T.warn}15`, marginBottom: 10, fontSize: 11 }}>
              <Clock size={10} /> {post.emberTTL}
            </div>
          )}

          {/* Content */}
          <p style={{ color: T.text, lineHeight: 1.65, marginBottom: post.media_urls?.length > 0 ? 10 : 16, fontSize: 14 }}>{post.content}</p>
          {post.media_urls && post.media_urls.length > 0 && (
            <div style={{ display:'grid', gridTemplateColumns: post.media_urls.length === 1 ? '1fr' : '1fr 1fr', gap:4, marginBottom:16, borderRadius:10, overflow:'hidden' }}>
              {post.media_urls.map((url, i) => (
                <div key={i} style={{ position:'relative', paddingBottom: post.media_urls.length === 1 ? '56.25%' : '100%', background:T.s3 }}>
                  <img
                    src={url}
                    alt=""
                    style={{ position:'absolute', top:0, left:0, width:'100%', height:'100%', objectFit:'cover', cursor:'pointer' }}
                    onClick={() => window.open(url, '_blank')}
                  />
                </div>
              ))}
            </div>
          )}

          {/* Actions */}
          <div style={{ display: 'flex', gap: 4, borderTop: `1px solid ${T.border}`, paddingTop: 12 }}>
            {[
              { icon: Heart, label: `${((post.post_likes?.[0]?.count || 0)) + (likedPosts.has(post.id) ? 1 : 0)}`, action: () => handleLike(post.id), active: likedPosts.has(post.id), activeColor: T.error },
              { icon: MessageCircle, label: `${post.post_comments?.[0]?.count || 0}`, action: () => handleOpenComments(post.id), active: openComments === post.id, activeColor: T.teal },
              { icon: Share2, label: `${post.shares_count || 0}`, action: () => dispatch({ type: 'ADD_TOAST', payload: { type: 'info', message: 'Link copied', icon: '🔗' } }), active: false },
              { icon: Bookmark, label: '', action: () => dispatch({ type: 'TOGGLE_SAVE', payload: post.id }), active: savedPosts.has(post.id), activeColor: T.gold },
            ].map(({ icon: Icon, label, action, active, activeColor }, i) => (
              <button
                key={i}
                onClick={action}
                style={{
                  ...S.btn, ...S.btnGhost, padding: '6px 12px', flex: 1, justifyContent: 'center',
                  color: active ? (activeColor || T.gold) : T.text2,
                  borderColor: active ? `${activeColor || T.gold}40` : T.border,
                }}
              >
                <Icon size={15} fill={active ? (activeColor || T.gold) : 'none'} />
                {label && <span style={{ fontSize: 12 }}>{label}</span>}
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
      {openComments && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.85)', zIndex:2000, display:'flex', alignItems:'flex-end', justifyContent:'center', padding:'0 1rem 1rem' }}>
          <div style={{ background:T.s1, border:`1px solid ${T.border2}`, borderRadius:16, width:'100%', maxWidth:580, maxHeight:'70vh', display:'flex', flexDirection:'column' }}>
            <div style={{ padding:'1rem 1.25rem', borderBottom:`1px solid ${T.border}`, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <span style={{ fontWeight:600, fontSize:14 }}>Comments</span>
              <button onClick={() => { setOpenComments(null); setComments([]); }} style={{ background:'none', border:'none', color:T.text2, fontSize:20, cursor:'pointer' }}>✕</button>
            </div>
            <div style={{ flex:1, overflowY:'auto', padding:'1rem' }}>
              {loadingComments && <div style={{ textAlign:'center', color:T.text3, fontSize:13 }}>Loading...</div>}
              {!loadingComments && comments.length === 0 && <div style={{ textAlign:'center', color:T.text3, fontSize:13, padding:'1rem 0' }}>No comments yet. Be the first.</div>}
              {comments.map(comment => (
                <div key={comment.id} style={{ display:'flex', gap:10, marginBottom:14 }}>
                  {comment.profiles?.avatar_url ? <img src={comment.profiles.avatar_url} alt="av" style={{ width:32, height:32, borderRadius:'50%', objectFit:'cover', flexShrink:0 }} /> : <Avatar emoji="👤" size={32} />}
                  <div style={{ flex:1, background:T.s2, borderRadius:10, padding:'0.5rem 0.75rem' }}>
                    <div style={{ fontSize:12, fontWeight:600, color:T.gold, marginBottom:2 }}>{comment.profiles?.display_name || comment.profiles?.handle}</div>
                    <div style={{ fontSize:13, color:T.text }}>{comment.content}</div>
                    <div style={{ fontSize:11, color:T.text3, marginTop:4 }}>{new Date(comment.created_at).toLocaleDateString()}</div>
                  </div>
                </div>
              ))}
            </div>
            <div style={{ padding:'0.75rem 1rem', borderTop:`1px solid ${T.border}`, display:'flex', gap:8 }}>
              <input value={commentInput} onChange={e => setCommentInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleAddComment()} placeholder="Add a comment..." style={{ ...S.input, flex:1 }} />
              <button onClick={handleAddComment} style={{ ...S.btn, ...S.btnGold, padding:'0.6rem 1rem' }}>Post</button>
            </div>
          </div>
        </div>
      )}
  </>);
}

// ── TALENT SEARCH VIEW ──
function SearchView({ state, dispatch, userId }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState({ profiles: [], jobs: [], castings: [] });
  const [searching, setSearching] = useState(false);
  const [searched, setSearched] = useState(false);

  const handleSearch = async (q) => {
    setQuery(q);
    if (q.trim().length < 2) { setResults({ profiles: [], jobs: [], castings: [] }); setSearched(false); return; }
    setSearching(true);
    const res = await globalSearch(q);
    setResults(res);
    setSearched(true);
    setSearching(false);
  };

  const total = results.profiles.length + results.jobs.length + results.castings.length;

  return (
    <div style={{ maxWidth: 680, margin: '0 auto' }}>
      <div style={{ position: 'relative', marginBottom: 24 }}>
        <Search size={16} color={T.text3} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)' }} />
        <input
          placeholder="Search people, jobs, castings..."
          value={query}
          onChange={e => handleSearch(e.target.value)}
          autoFocus
          style={{ ...S.input, paddingLeft: 42, fontSize: 15, padding: '0.9rem 1rem 0.9rem 42px' }}
        />
        {searching && <div style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', color: T.text3, fontSize: 12 }}>Searching...</div>}
      </div>

      {!searched && <div style={{ textAlign: 'center', color: T.text3, fontSize: 14, marginTop: 60 }}>Search across people, jobs, and castings</div>}
      {searched && total === 0 && <div style={{ textAlign: 'center', color: T.text3, fontSize: 14, marginTop: 60 }}>No results for "{query}"</div>}

      {results.profiles.length > 0 && (
        <div style={{ ...S.card, marginBottom: 16 }}>
          <div style={{ fontSize: 11, color: T.text3, textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 12 }}>People</div>
          {results.profiles.map(p => (
            <div key={p.id} onClick={() => dispatch({ type: 'SET_VIEW', payload: 'talent' })} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '0.65rem 0', borderBottom: `1px solid ${T.border}`, cursor: 'pointer' }}>
              <div style={{ width: 36, height: 36, borderRadius: '50%', background: `${T.gold}20`, border: `1px solid ${T.gold}40`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, overflow: 'hidden' }}>
                {p.avatar_url ? <img src={p.avatar_url} style={{ width: '100%', height: '100%', objectFit: 'cover' }} alt="" /> : '👤'}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: 13 }}>{p.display_name || p.handle}</div>
                <div style={{ fontSize: 11, color: T.text2 }}>{p.handle} · {(p.crafts || []).slice(0, 3).join(', ')}</div>
              </div>
              <ChevronRight size={14} color={T.text3} />
            </div>
          ))}
        </div>
      )}

      {results.jobs.length > 0 && (
        <div style={{ ...S.card, marginBottom: 16 }}>
          <div style={{ fontSize: 11, color: T.text3, textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 12 }}>Jobs</div>
          {results.jobs.map(j => (
            <div key={j.id} onClick={() => dispatch({ type: 'SELECT_JOB', payload: j })} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '0.65rem 0', borderBottom: `1px solid ${T.border}`, cursor: 'pointer' }}>
              <div style={{ width: 36, height: 36, borderRadius: 8, background: `${T.purple}20`, border: `1px solid ${T.purple}40`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Briefcase size={16} color={T.purple} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: 13 }}>{j.title}</div>
                <div style={{ fontSize: 11, color: T.text2 }}>{j.type} · {j.rate} · {j.location}</div>
              </div>
              <ChevronRight size={14} color={T.text3} />
            </div>
          ))}
        </div>
      )}

      {results.castings.length > 0 && (
        <div style={{ ...S.card, marginBottom: 16 }}>
          <div style={{ fontSize: 11, color: T.text3, textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 12 }}>Castings</div>
          {results.castings.map(cs => (
            <div key={cs.id} onClick={() => dispatch({ type: 'SET_VIEW', payload: 'casting' })} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '0.65rem 0', borderBottom: `1px solid ${T.border}`, cursor: 'pointer' }}>
              <div style={{ width: 36, height: 36, borderRadius: 8, background: `${T.teal}20`, border: `1px solid ${T.teal}40`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Layers size={16} color={T.teal} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: 13 }}>{cs.title}</div>
                <div style={{ fontSize: 11, color: T.text2 }}>{cs.type} · {cs.rate} · {cs.location}</div>
              </div>
              <ChevronRight size={14} color={T.text3} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function TalentView({ state, dispatch, realProfiles, userId }) {
  const windowWidth = useWindowWidth();
  const isMobile = windowWidth < 768;
  const talents = realProfiles && realProfiles.length > 0
    ? realProfiles
        .filter(p => p.id !== userId)
        .map(p => ({
          id: p.id,
          name: p.display_name || p.handle || 'Unknown',
          handle: p.handle || '',
          avatar: '👤',
          avatarUrl: p.avatar_url || null,
          crafts: p.crafts || [],
          location: p.location || 'Location not set',
          verScore: 50,
          craftScore: 50,
          available: p.available !== false,
          rate: 'Contact for rate',
        }))
    : null;
  const [query, setQuery] = useState('');
  const [filterCraft, setFilterCraft] = useState('all');
  const [filterAvail, setFilterAvail] = useState(false);

  const crafts = ['all', 'Actor', 'Model', 'Singer', 'Dancer', 'Photographer', 'Videographer', 'Director', 'Producer', 'Stage Manager', 'Costume Designer', 'Stylist', 'Lighting Designer', 'Sound Designer', 'Writer', 'Composer', 'Musician', 'DP'];
  const talentList = talents || [];
  const filtered = talentList.filter(t => {
    const matchesQuery = !query || (t.name||'').toLowerCase().includes(query.toLowerCase()) || (t.crafts||[]).some(c => c.toLowerCase().includes(query.toLowerCase()));
    const matchesCraft = filterCraft === 'all' || t.crafts.includes(filterCraft);
    const matchesAvail = !filterAvail || t.available;
    return matchesQuery && matchesCraft && matchesAvail;
  });

  const handleBook = (talent) => {
    dispatch({ type: 'ADD_TOAST', payload: { type: 'success', message: `Booking request sent to ${talent.name}`, icon: '✦' } });
  };
  const [selectedTalent, setSelectedTalent] = useState(null);
  const [isFollowing, setIsFollowing] = useState(false);
  const [followLoading, setFollowLoading] = useState(false);

  const handleViewTalent = async (talent) => {
    if (userId && talent.id) {
      recordProfileView(talent.id, userId);
      const status = await getFollowStatus(userId, talent.id);
      setIsFollowing(status);
    }
    setSelectedTalent(talent);
  };

  const handleFollowToggle = async () => {
    if (!userId || !selectedTalent) return;
    setFollowLoading(true);
    if (isFollowing) {
      await unfollowUser(userId, selectedTalent.id);
      setIsFollowing(false);
    } else {
      await followUser(userId, selectedTalent.id);
      await createNotification(selectedTalent.id, 'follow', 'New Follower', `${state.user.name} started following you`, userId, null);
      setIsFollowing(true);
    }
    setFollowLoading(false);
  };

  return (
    <div>
      {selectedTalent && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.85)', zIndex:2000, display:'flex', alignItems:'center', justifyContent:'center', padding:'1rem' }}>
          <div style={{ background:T.s1, border:`1px solid ${T.border2}`, borderRadius:16, width:'100%', maxWidth:480, padding:'1.5rem' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:16 }}>
              <div style={{ display:'flex', gap:12, alignItems:'center' }}>
                {selectedTalent.avatarUrl
                  ? <img src={selectedTalent.avatarUrl} alt="avatar" style={{ width:56, height:56, borderRadius:'50%', objectFit:'cover', border:`2px solid ${T.gold}40` }} />
                  : <Avatar emoji={selectedTalent.avatar} size={56} border />
                }
                <div>
                  <div style={{ fontWeight:700, fontSize:18, color:T.cream }}>{selectedTalent.name}</div>
                  <div style={{ fontSize:13, color:T.text2 }}>{selectedTalent.handle}</div>
                  <div style={{ fontSize:12, color:T.text3, marginTop:2 }}>{selectedTalent.location}</div>
                </div>
              </div>
              <button onClick={() => setSelectedTalent(null)} style={{ background:'none', border:'none', color:T.text2, fontSize:20, cursor:'pointer' }}>✕</button>
            </div>
            <div style={{ display:'flex', flexWrap:'wrap', gap:6, marginBottom:14 }}>
              {(selectedTalent.crafts || []).map(c => <Badge key={c} color={T.gold} bg={`${T.gold}15`}>{c}</Badge>)}
            </div>
            <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
              <button
                onClick={handleFollowToggle}
                disabled={followLoading}
                style={{ ...S.btn, ...(isFollowing ? S.btnGhost : S.btnGold), flex:1, justifyContent:'center', opacity: followLoading ? 0.6 : 1 }}
              >
                {followLoading ? '...' : isFollowing ? 'Unfollow' : '+ Follow'}
              </button>
              <button
                onClick={() => {
                  if (userId) sendConnectionRequest(userId, selectedTalent.id).then(() => {
                    dispatch({ type: 'ADD_TOAST', payload: { type: 'success', message: `Connection request sent to ${selectedTalent.name}`, icon: '✦' } });
                  });
                  setSelectedTalent(null);
                }}
                style={{ ...S.btn, ...S.btnGhost, flex:1, justifyContent:'center' }}
              >
                Connect
              </button>
              <button
                onClick={() => {
                  dispatch({ type: 'SET_VIEW', payload: 'messages' });
                  setSelectedTalent(null);
                }}
                style={{ ...S.btn, ...S.btnGhost, flex:1, justifyContent:'center' }}
              >
                Message
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="talent-filter-bar" style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
          <Search size={15} color={T.text3} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)' }} />
          <input
            placeholder="Search talent by name or craft..."
            value={query} onChange={e => setQuery(e.target.value)}
            style={{ ...S.input, paddingLeft: 36 }}
          />
        </div>
        <select
          value={filterCraft} onChange={e => setFilterCraft(e.target.value)}
          style={{ ...S.input, width: isMobile ? '100%' : 'auto', cursor: 'pointer' }}
        >
          {crafts.map(c => <option key={c} value={c}>{c === 'all' ? 'All Crafts' : c}</option>)}
        </select>
        <button
          onClick={() => setFilterAvail(!filterAvail)}
          style={{ ...S.btn, ...(filterAvail ? S.btnGold : S.btnGhost), flexShrink: 0, width: isMobile ? '100%' : 'auto', justifyContent: 'center', marginLeft: 0 }}
        >
          <Zap size={14} /> {isMobile ? "Available" : "Available Now"}
        </button>
      </div>

      <div className="talent-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 }}>
        {filtered.length === 0 && (
          <div style={{ gridColumn:'1/-1', textAlign:'center', padding:'4rem 2rem', color:T.text3 }}>
            <div style={{ fontSize:36, marginBottom:12 }}>✦</div>
            <div style={{ fontSize:15, fontWeight:600, color:T.text2, marginBottom:6 }}>No talent found</div>
            <div style={{ fontSize:13 }}>Try a different search or craft filter.</div>
          </div>
        )}
        {filtered.map(talent => (
          <div key={talent.id} style={{ ...S.card, cursor: 'pointer' }}
            onClick={() => handleViewTalent(talent)}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 14 }}>
              {talent.avatarUrl ? <img src={talent.avatarUrl} alt="avatar" style={{width:48,height:48,borderRadius:'50%',objectFit:'cover',border:`2px solid ${T.gold}40`}} /> : <Avatar emoji={talent.avatar} size={48} border />}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 15 }}>{talent.name}</div>
                <div style={{ fontSize: 12, color: T.text2, marginBottom: 6 }}>{talent.handle}</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                  {talent.crafts.map(c => (
                    <Badge key={c} color={T.text2} bg={T.s3}>{c}</Badge>
                  ))}
                </div>
              </div>
              <div style={{ ...S.badge, color: talent.available ? T.success : T.text3, background: talent.available ? `${T.success}15` : T.s4, fontSize: 11 }}>
                {talent.available ? '● Available' : '○ Unavailable'}
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 14 }}>
              <div style={{ background: T.s3, borderRadius: 8, padding: '0.6rem' }}>
                <div style={{ fontSize: 11, color: T.text3, marginBottom: 4 }}>VERIFICATION</div>
                <ScoreBar value={talent.verScore} color={T.teal} />
                <div style={{ fontSize: 13, fontWeight: 700, color: T.teal, marginTop: 4, fontFamily: 'DM Mono, monospace' }}>{talent.verScore}</div>
              </div>
              <div style={{ background: T.s3, borderRadius: 8, padding: '0.6rem' }}>
                <div style={{ fontSize: 11, color: T.text3, marginBottom: 4 }}>CRAFT LINE</div>
                <ScoreBar value={talent.craftScore} color={T.gold} />
                <div style={{ fontSize: 13, fontWeight: 700, color: T.gold, marginTop: 4, fontFamily: 'DM Mono, monospace' }}>{talent.craftScore}</div>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ fontSize: 13, color: T.text2 }}>
                <MapPin size={11} style={{ marginRight: 4 }} />{talent.location}
              </div>
              <div style={{ fontSize: 14, fontWeight: 700, color: T.gold }}>{talent.rate}</div>
            </div>

            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              <button
                style={{ ...S.btn, ...S.btnGhost, flex: 1, justifyContent: 'center' }}
                onClick={e => { e.stopPropagation(); dispatch({ type: 'SET_VIEW', payload: 'messages' }); }}
              >
                <MessageCircle size={14} /> Message
              </button>
              <button
                style={{ ...S.btn, ...S.btnGold, flex: 1, justifyContent: 'center' }}
                onClick={e => { e.stopPropagation(); handleBook(talent); }}
              >
                <Briefcase size={14} /> Book
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── JOBS VIEW ──
function JobsView({ state, dispatch, realJobs, loadRealJobs, user }) {
  const windowWidth = useWindowWidth();
  const isMobile = windowWidth < 768;
  const [applying, setApplying] = useState(false);
  const [applicants, setApplicants] = useState([]);
  const [loadingApplicants, setLoadingApplicants] = useState(false);
  const [applied, setApplied] = useState(false);
  const [applyNote, setApplyNote] = useState('');
  const [submitOnBehalfOf, setSubmitOnBehalfOf] = useState(null);
  const [roster, setRoster] = useState([]);

  // Load roster if agent or manager
  useEffect(() => {
    if (!userId) return;
    const accountType = state.user?.account_type;
    if (accountType === 'agent' || accountType === 'manager') {
      const saved = localStorage.getItem('roster_' + userId);
      if (saved) setRoster(JSON.parse(saved).filter(r => r.status === 'active'));
    }
  }, [userId, state.user?.account_type]);
  const [showApplyModal, setShowApplyModal] = useState(false);

  const handleApplyToJob = async (jobId) => {
    const userId = state.user?.id;
    if (!userId) return;
    setApplying(true);
    const result = await applyToJob(jobId, userId, applyNote);
    if (result) {
      setApplied(true);
      setShowApplyModal(false);
      setApplyNote('');
      dispatch({ type: 'ADD_TOAST', payload: { type: 'success', message: 'Application submitted! ✦', icon: '✦' } });
    }
    setApplying(false);
  };
  const jobs = realJobs || [];
  const [showForm, setShowForm] = useState(false);
  const [showFilter, setShowFilter] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    type: 'all',
    craft: 'all',
    location: '',
    rateRange: 'all',
    duration: 'all',
    setting: 'all',
    union: 'all',
    experience: 'all',
    postedDate: 'all',
    urgentOnly: false,
    paidOnly: false,
    creditEligible: false,
    verifiedOnly: false,
    beginnerFriendly: false,
    recurringOnly: false,
    savedOnly: false,
  });

  const setFilter = (key, val) => setFilters(f => ({ ...f, [key]: val }));
  const toggleFilter = (key) => setFilters(f => ({ ...f, [key]: !f[key] }));

  const clearFilters = () => setFilters({
    type:'all',craft:'all',location:'',rateRange:'all',duration:'all',
    setting:'all',union:'all',experience:'all',postedDate:'all',
    urgentOnly:false,paidOnly:false,creditEligible:false,
    verifiedOnly:false,beginnerFriendly:false,recurringOnly:false,savedOnly:false,
  });

  const activeFilterCount = Object.entries(filters).filter(([k,v]) =>
    v !== 'all' && v !== '' && v !== false
  ).length;

  const filteredJobs = jobs.filter(job => {
    if (filters.type !== 'all' && job.type !== filters.type) return false;
    if (filters.craft !== 'all' && !(job.skills || []).includes(filters.craft)) return false;
    if (filters.location && !(job.location || '').toLowerCase().includes(filters.location.toLowerCase())) return false;
    if (filters.urgentOnly && !job.urgent) return false;
    if (filters.postedDate !== 'all') {
      const created = new Date(job.created_at || Date.now());
      const now = new Date();
      const diffDays = (now - created) / (1000 * 60 * 60 * 24);
      if (filters.postedDate === 'today' && diffDays > 1) return false;
      if (filters.postedDate === 'week' && diffDays > 7) return false;
      if (filters.postedDate === 'month' && diffDays > 30) return false;
    }
    if (searchQuery && !(job.title || '').toLowerCase().includes(searchQuery.toLowerCase()) &&
        !(job.description || '').toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  const CRAFTS_LIST = ['Actor','Model','Singer','Dancer','Photographer','Videographer','Director','Producer','Stage Manager','Costume Designer','Stylist','Lighting Designer','Sound Designer','Writer','Composer','Musician','DP'];

  const ToggleChip = ({ label, filterKey }) => (
    <button
      onClick={() => toggleFilter(filterKey)}
      style={{
        padding:'5px 12px', borderRadius:20, fontSize:12, fontWeight:600,
        cursor:'pointer', fontFamily:'inherit',
        background: filters[filterKey] ? `${T.gold}20` : T.s3,
        border: `1px solid ${filters[filterKey] ? T.gold : T.border2}`,
        color: filters[filterKey] ? T.gold : T.text2,
        transition:'all .15s', minHeight:32,
      }}
    >
      {label}
    </button>
  );
  const [jobTitle, setJobTitle] = useState('');
  const [jobType, setJobType] = useState('Film');
  const [jobRate, setJobRate] = useState('');
  const [jobDuration, setJobDuration] = useState('');
  const [jobLocation, setJobLocation] = useState('');
  const [jobDesc, setJobDesc] = useState('');
  const [jobSkills, setJobSkills] = useState('');
  const [jobUrgent, setJobUrgent] = useState(false);
  const [posting, setPosting] = useState(false);

  const handlePostJob = async () => {
    if (!jobTitle.trim()) return;
    setPosting(true);
    try {
      const { createJob } = await import('./db');
      await createJob(state.user.id, {
        title: jobTitle,
        type: jobType,
        rate: jobRate,
        duration: jobDuration,
        location: jobLocation,
        description: jobDesc,
        skills: jobSkills.split(',').map(s => s.trim()).filter(Boolean),
        urgent: jobUrgent,
        status: 'active',
      });
      if (loadRealJobs) await loadRealJobs();
      setShowForm(false);
      setJobTitle(''); setJobRate(''); setJobDuration('');
      setJobLocation(''); setJobDesc(''); setJobSkills('');
      dispatch({ type: 'ADD_TOAST', payload: { type: 'success', message: 'Job posted successfully ✦', icon: '✦' } });
    } catch(e) {
      dispatch({ type: 'ADD_TOAST', payload: { type: 'error', message: 'Failed to post job', icon: '✕' } });
    }
    setPosting(false);
  };
  const { selectedJob } = state;
  useEffect(() => {
    if (selectedJob && selectedJob.poster_id === state.user?.id) {
      setLoadingApplicants(true);
      getJobApplicants(selectedJob.id).then(data => { setApplicants(data); setLoadingApplicants(false); });
    } else {
      setApplicants([]);
    }
  }, [selectedJob?.id, state.user?.id]);

  if (selectedJob) {
    return (
      <div style={{ maxWidth: 720, margin: '0 auto' }}>
        <button
          style={{ ...S.btn, ...S.btnGhost, marginBottom: 20 }}
          onClick={() => dispatch({ type: 'SELECT_JOB', payload: null })}
        >
          <ArrowLeft size={14} /> Back to Jobs
        </button>
        <div style={S.card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
            <div>
              <h2 style={{ fontSize: 22, fontFamily: 'Cormorant Garamond, serif', fontWeight: 700, marginBottom: 6 }}>{selectedJob.title}</h2>
              <div style={{ fontSize: 13, color: T.text2 }}>{selectedJob.poster} · {selectedJob.type} · {selectedJob.posted}</div>
            </div>
            {selectedJob.urgent && <Badge color={T.error} bg={`${T.error}18`}>Urgent</Badge>}
          </div>

          <div className="job-detail-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 20 }}>
            {[
              { icon: DollarSign, label: 'Rate', value: selectedJob.rate },
              { icon: Clock, label: 'Duration', value: selectedJob.duration },
              { icon: MapPin, label: 'Location', value: selectedJob.location },
            ].map(({ icon: Icon, label, value }) => (
              <div key={label} style={{ background: T.s3, borderRadius: 8, padding: '0.8rem' }}>
                <div style={{ fontSize: 11, color: T.text3, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
                  <Icon size={10} /> {label}
                </div>
                <div style={{ fontSize: 14, fontWeight: 600 }}>{value}</div>
              </div>
            ))}
          </div>

          <div style={{ marginBottom: 20 }}>
            <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 8, color: T.text2 }}>Description</h3>
            <p style={{ lineHeight: 1.7, color: T.text, fontSize: 14 }}>{selectedJob.description}</p>
          </div>

          <div style={{ marginBottom: 24 }}>
            <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 10, color: T.text2 }}>Skills Required</h3>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {(selectedJob.skills || []).map(s => <Badge key={s} color={T.gold} bg={`${T.gold}15`}>{s}</Badge>)}
            </div>
          </div>

          <div style={{ display: 'flex', gap: 12 }}>
            {selectedJob.poster_id !== state.user?.id && (
            <button
              style={{ ...S.btn, ...(applied ? S.btnGhost : S.btnGold), flex: 1, justifyContent: 'center', padding: '0.75rem', opacity: applied ? 0.7 : 1 }}
              onClick={() => !applied && setShowApplyModal(true)}
              disabled={applied}
            >
              <Send size={15} /> {applied ? 'Applied ✓' : 'Apply Now'}
            </button>
            )}
            {showApplyModal && (
              <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.85)', zIndex:2000, display:'flex', alignItems:'center', justifyContent:'center', padding:'1rem' }}>
                <div style={{ background:T.s1, border:`1px solid ${T.border2}`, borderRadius:16, width:'100%', maxWidth:460, padding:'1.5rem' }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
                    <h3 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:20, color:T.gold, margin:0 }}>Apply for this role</h3>
                    <button onClick={() => setShowApplyModal(false)} style={{ background:'none', border:'none', color:T.text2, fontSize:20, cursor:'pointer' }}>✕</button>
                  </div>
                  <textarea
                    placeholder="Add a note to your application (optional)..."
                    value={applyNote}
                    onChange={e => setApplyNote(e.target.value)}
                    rows={4}
                    style={{ ...S.input, resize:'vertical', width:'100%', marginBottom:12 }}
                  />
                  <div style={{ display:'flex', gap:10 }}>
                    <button onClick={() => setShowApplyModal(false)} style={{ flex:1, ...S.btn, ...S.btnGhost }}>Cancel</button>
                    <button onClick={() => handleApplyToJob(state.selectedJob?.id)} disabled={applying} style={{ flex:2, ...S.btn, ...S.btnGold, opacity:applying?0.6:1 }}>
                      {applying ? 'Submitting...' : 'Submit Application ✦'}
                    </button>
                  </div>
                </div>
              </div>
            )}
            <button style={{ ...S.btn, ...S.btnGhost }}
              onClick={() => dispatch({ type: 'ADD_TOAST', payload: { type: 'info', message: 'Job saved', icon: '🔖' } })}>
              <Bookmark size={15} />
            </button>
          </div>
        </div>
        {selectedJob.poster_id === state.user?.id && (
          <div style={{ ...S.card, marginTop: 16 }}>
            <h3 style={{ fontSize: 16, fontFamily:"'Cormorant Garamond',serif", color: T.gold, marginBottom: 16 }}>Applicants {applicants.length > 0 && `(${applicants.length})`}</h3>
            {loadingApplicants && <div style={{ color: T.text3, fontSize: 13 }}>Loading...</div>}
            {!loadingApplicants && applicants.length === 0 && <div style={{ color: T.text3, fontSize: 13 }}>No applications yet.</div>}
            {applicants.map(app => (
              <div key={app.id} style={{ display:"flex", alignItems:"center", gap:12, padding:"0.75rem 0", borderBottom:`1px solid ${T.border}` }}>
                <div style={{ width:36, height:36, borderRadius:"50%", background:`${T.gold}20`, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                  {app.applicant?.avatar_url ? <img src={app.applicant.avatar_url} style={{width:"100%",height:"100%",borderRadius:"50%",objectFit:"cover"}} alt="" /> : "👤"}
                </div>
                <div style={{ flex:1 }}>
                  <div style={{ fontWeight:600, fontSize:13 }}>{app.applicant?.display_name || app.applicant?.handle || "Unknown"}</div>
                  <div style={{ fontSize:11, color:T.text2 }}>{(app.applicant?.crafts || []).join(", ")}</div>
                  {app.note && <div style={{ fontSize:12, color:T.text3, marginTop:4, fontStyle:"italic" }}>"{app.note}"</div>}
                </div>
                <div style={{ fontSize:11, color:T.text3 }}>{app.applied_at ? new Date(app.applied_at).toLocaleDateString() : ""}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap', padding: isMobile ? '0 4px' : 0 }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 150 }}>
          <Search size={15} color={T.text3} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)' }} />
          <input placeholder="Search jobs..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} style={{ ...S.input, paddingLeft: 36 }} />
        </div>
        <button
          style={{ ...S.btn, ...(activeFilterCount > 0 ? S.btnGold : S.btnGhost), whiteSpace: 'nowrap' }}
          onClick={() => setShowFilter(!showFilter)}
        >
          <Filter size={14} /> {isMobile ? '' : 'Filter '}{activeFilterCount > 0 ? `(${activeFilterCount})` : (isMobile ? 'Filter' : '')}
        </button>
        <button style={{ ...S.btn, ...S.btnGold, whiteSpace: 'nowrap' }} onClick={() => setShowForm(!showForm)}><Plus size={14} /> {showForm ? 'Cancel' : (isMobile ? 'Post' : 'Post a Job')}</button>
      </div>

      {showFilter && (
        <div style={{ ...S.card, border: `1px solid ${T.gold}40`, marginBottom: 16 }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
            <span style={{ fontSize:14, fontWeight:700, color:T.gold }}>Filter Jobs</span>
            <div style={{ display:'flex', gap:8 }}>
              {activeFilterCount > 0 && <button onClick={clearFilters} style={{ ...S.btn, ...S.btnGhost, fontSize:11, padding:'4px 10px' }}>Clear all ({activeFilterCount})</button>}
              <button onClick={() => setShowFilter(false)} style={{ ...S.btn, ...S.btnGhost, fontSize:11, padding:'4px 10px' }}>Done</button>
            </div>
          </div>

          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:14 }}>
            <div>
              <label style={{ fontSize:11, color:T.text3, textTransform:'uppercase', letterSpacing:'.06em', display:'block', marginBottom:5 }}>Job Type</label>
              <select value={filters.type} onChange={e => setFilter('type', e.target.value)} style={{ ...S.input, cursor:'pointer' }}>
                <option value="all">All Types</option>
                {['Film','Theatre','Fashion','Commercial','Music','Photography','Events','Other'].map(t => <option key={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize:11, color:T.text3, textTransform:'uppercase', letterSpacing:'.06em', display:'block', marginBottom:5 }}>Craft Required</label>
              <select value={filters.craft} onChange={e => setFilter('craft', e.target.value)} style={{ ...S.input, cursor:'pointer' }}>
                <option value="all">All Crafts</option>
                {CRAFTS_LIST.map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize:11, color:T.text3, textTransform:'uppercase', letterSpacing:'.06em', display:'block', marginBottom:5 }}>Rate Range</label>
              <select value={filters.rateRange} onChange={e => setFilter('rateRange', e.target.value)} style={{ ...S.input, cursor:'pointer' }}>
                <option value="all">Any Rate</option>
                <option value="unpaid">Unpaid / Deferred</option>
                <option value="low">Under $100/day</option>
                <option value="mid">$100–$500/day</option>
                <option value="high">$500+/day</option>
                <option value="negotiable">Negotiable</option>
              </select>
            </div>
            <div>
              <label style={{ fontSize:11, color:T.text3, textTransform:'uppercase', letterSpacing:'.06em', display:'block', marginBottom:5 }}>Duration</label>
              <select value={filters.duration} onChange={e => setFilter('duration', e.target.value)} style={{ ...S.input, cursor:'pointer' }}>
                <option value="all">Any Duration</option>
                <option value="day">Single Day</option>
                <option value="week">Under 1 Week</option>
                <option value="month">1–4 Weeks</option>
                <option value="ongoing">Ongoing</option>
              </select>
            </div>
            <div>
              <label style={{ fontSize:11, color:T.text3, textTransform:'uppercase', letterSpacing:'.06em', display:'block', marginBottom:5 }}>Setting</label>
              <select value={filters.setting} onChange={e => setFilter('setting', e.target.value)} style={{ ...S.input, cursor:'pointer' }}>
                <option value="all">Any Setting</option>
                <option value="in-person">In-Person</option>
                <option value="remote">Remote</option>
                <option value="hybrid">Hybrid</option>
              </select>
            </div>
            <div>
              <label style={{ fontSize:11, color:T.text3, textTransform:'uppercase', letterSpacing:'.06em', display:'block', marginBottom:5 }}>Union Status</label>
              <select value={filters.union} onChange={e => setFilter('union', e.target.value)} style={{ ...S.input, cursor:'pointer' }}>
                <option value="all">Union or Non-Union</option>
                <option value="union">Union Only</option>
                <option value="non-union">Non-Union Only</option>
                <option value="both">Open to Both</option>
              </select>
            </div>
            <div>
              <label style={{ fontSize:11, color:T.text3, textTransform:'uppercase', letterSpacing:'.06em', display:'block', marginBottom:5 }}>Experience Level</label>
              <select value={filters.experience} onChange={e => setFilter('experience', e.target.value)} style={{ ...S.input, cursor:'pointer' }}>
                <option value="all">Any Level</option>
                <option value="entry">Entry Level</option>
                <option value="mid">Mid Level</option>
                <option value="senior">Senior</option>
              </select>
            </div>
            <div>
              <label style={{ fontSize:11, color:T.text3, textTransform:'uppercase', letterSpacing:'.06em', display:'block', marginBottom:5 }}>Posted</label>
              <select value={filters.postedDate} onChange={e => setFilter('postedDate', e.target.value)} style={{ ...S.input, cursor:'pointer' }}>
                <option value="all">Any Time</option>
                <option value="today">Today</option>
                <option value="week">This Week</option>
                <option value="month">This Month</option>
              </select>
            </div>
          </div>

          <div>
            <label style={{ fontSize:11, color:T.text3, textTransform:'uppercase', letterSpacing:'.06em', display:'block', marginBottom:8 }}>Location</label>
            <input value={filters.location} onChange={e => setFilter('location', e.target.value)} placeholder="City, state, or region..." style={{ ...S.input, marginBottom:12 }} />
          </div>

          <div>
            <label style={{ fontSize:11, color:T.text3, textTransform:'uppercase', letterSpacing:'.06em', display:'block', marginBottom:8 }}>Quick Filters</label>
            <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
              <ToggleChip label="🚨 Urgent Only" filterKey="urgentOnly" />
              <ToggleChip label="💰 Paid Only" filterKey="paidOnly" />
              <ToggleChip label="🎬 Credit Eligible" filterKey="creditEligible" />
              <ToggleChip label="🛡 Verified Posters" filterKey="verifiedOnly" />
              <ToggleChip label="🌱 Beginner Friendly" filterKey="beginnerFriendly" />
              <ToggleChip label="🔄 Recurring Work" filterKey="recurringOnly" />
              <ToggleChip label="🔖 Saved Jobs" filterKey="savedOnly" />
            </div>
          </div>

          <div style={{ marginTop:14, paddingTop:12, borderTop:`1px solid ${T.border}`, fontSize:12, color:T.text2 }}>
            Showing <span style={{ color:T.gold, fontWeight:700 }}>{filteredJobs.length}</span> of {jobs.length} jobs
          </div>
        </div>
      )}

      {showForm && (
        <div style={{ ...S.card, border: `1px solid ${T.gold}40`, marginBottom: 20 }}>
          <h3 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 20, color: T.gold, marginBottom: 16 }}>Post a Job</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
            <div>
              <label style={{ fontSize: 11, color: T.text3, textTransform: 'uppercase', letterSpacing: '.06em', display: 'block', marginBottom: 5 }}>Job Title</label>
              <input value={jobTitle} onChange={e => setJobTitle(e.target.value)} placeholder="e.g. DP for Short Film" style={{ ...S.input }} />
            </div>
            <div>
              <label style={{ fontSize: 11, color: T.text3, textTransform: 'uppercase', letterSpacing: '.06em', display: 'block', marginBottom: 5 }}>Type</label>
              <select value={jobType} onChange={e => setJobType(e.target.value)} style={{ ...S.input, cursor: 'pointer' }}>
                {['Film','Theatre','Fashion','Commercial','Music','Photography','Events','Other'].map(t => <option key={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize: 11, color: T.text3, textTransform: 'uppercase', letterSpacing: '.06em', display: 'block', marginBottom: 5 }}>Rate</label>
              <input value={jobRate} onChange={e => setJobRate(e.target.value)} placeholder="e.g. $500/day" style={{ ...S.input }} />
            </div>
            <div>
              <label style={{ fontSize: 11, color: T.text3, textTransform: 'uppercase', letterSpacing: '.06em', display: 'block', marginBottom: 5 }}>Duration</label>
              <input value={jobDuration} onChange={e => setJobDuration(e.target.value)} placeholder="e.g. 3 days" style={{ ...S.input }} />
            </div>
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 11, color: T.text3, textTransform: 'uppercase', letterSpacing: '.06em', display: 'block', marginBottom: 5 }}>Location</label>
            <input value={jobLocation} onChange={e => setJobLocation(e.target.value)} placeholder="City, State" style={{ ...S.input }} />
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 11, color: T.text3, textTransform: 'uppercase', letterSpacing: '.06em', display: 'block', marginBottom: 5 }}>Description</label>
            <textarea value={jobDesc} onChange={e => setJobDesc(e.target.value)} rows={3} placeholder="Describe the role, requirements, and what you're looking for..." style={{ ...S.input, resize: 'vertical', minHeight: 80 }} />
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 11, color: T.text3, textTransform: 'uppercase', letterSpacing: '.06em', display: 'block', marginBottom: 5 }}>Skills Required (comma-separated)</label>
            <input value={jobSkills} onChange={e => setJobSkills(e.target.value)} placeholder="e.g. Cinematography, Sony Alpha, Lighting" style={{ ...S.input }} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
            <button onClick={() => setJobUrgent(!jobUrgent)} style={{ width: 40, height: 22, borderRadius: 11, position: 'relative', cursor: 'pointer', border: 'none', background: jobUrgent ? T.error : T.s4, transition: 'background .2s' }}>
              <div style={{ position: 'absolute', top: 3, left: jobUrgent ? 21 : 3, width: 14, height: 14, borderRadius: '50%', background: 'white', transition: 'left .2s' }} />
            </button>
            <span style={{ fontSize: 13, color: T.text2 }}>Mark as urgent</span>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={() => setShowForm(false)} style={{ ...S.btn, ...S.btnGhost }}>Cancel</button>
            <button onClick={handlePostJob} disabled={posting} style={{ ...S.btn, ...S.btnGold, opacity: posting ? 0.7 : 1 }}>
              {posting ? 'Posting...' : 'Post Job'}
            </button>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {filteredJobs.length === 0 && (
          <div style={{ textAlign:'center', padding:'4rem 2rem', color:T.text3 }}>
            <div style={{ fontSize:36, marginBottom:12 }}>✦</div>
            <div style={{ fontSize:15, fontWeight:600, color:T.text2, marginBottom:6 }}>No jobs found</div>
            <div style={{ fontSize:13 }}>Try adjusting your filters or post the first opportunity.</div>
          </div>
        )}
        {filteredJobs.map(job => (
          <div
            key={job.id}
            style={{ ...S.card, cursor: 'pointer', transition: 'border-color 0.15s' }}
            onClick={() => dispatch({ type: 'SELECT_JOB', payload: job })}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <span style={{ fontWeight: 600, fontSize: 15 }}>{job.title}</span>
                  {job.urgent && <Badge color={T.error} bg={`${T.error}15`}>Urgent</Badge>}
                </div>
                <div style={{ fontSize: 13, color: T.text2 }}>{job.poster} · Posted {job.posted}</div>
              </div>
              <ChevronRight size={16} color={T.text3} />
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <Badge color={T.text2} bg={T.s3}>{job.type}</Badge>
              <Badge color={T.gold} bg={`${T.gold}12`}>{job.rate}</Badge>
              <Badge color={T.text2} bg={T.s3}><Clock size={10} /> {job.duration}</Badge>
              <Badge color={T.text2} bg={T.s3}><MapPin size={10} /> {job.location}</Badge>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


// ── CASTING VIEW ──
function CastingView({ state, dispatch, userId, verificationScore }) {
  const [castings, setCastings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPost, setShowPost] = useState(false);
  const [selectedCasting, setSelectedCasting] = useState(null);
  const [applications, setApplications] = useState([]);
  const [applyNote, setApplyNote] = useState('');
  const [submitOnBehalfOf, setSubmitOnBehalfOf] = useState(null);
  const [roster, setRoster] = useState([]);

  // Load roster if agent or manager
  useEffect(() => {
    if (!userId) return;
    const accountType = state.user?.account_type;
    if (accountType === 'agent' || accountType === 'manager') {
      const saved = localStorage.getItem('roster_' + userId);
      if (saved) setRoster(JSON.parse(saved).filter(r => r.status === 'active'));
    }
  }, [userId, state.user?.account_type]);
  const [applying, setApplying] = useState(false);
  const [applicants, setApplicants] = useState([]);
  const [loadingApplicants, setLoadingApplicants] = useState(false);
  const [posting, setPosting] = useState(false);
  const [form, setForm] = useState({ title:'', description:'', type:'paid', location:'', rate:'', spots:1, requireSelfTape:false, sideTitle:'', maxDuration:120, slateRequired:true, tapeFormat:'Horizontal (16:9)', tapeNotes:'' });

  useEffect(() => {
    getCastings().then(data => {
      try {
        const mock = JSON.parse(sessionStorage.getItem('artizan_tour_mock') || 'null');
        const mockCastings = mock?.casting || [];
        setCastings([...mockCastings, ...(data || [])]);
      } catch { setCastings(data || []); }
      setLoading(false);
    });
  }, []);

  const handleSelectCasting = async (casting) => {
    setSelectedCasting(casting);
    setApplyNote('');
    if (casting.posted_by === userId) {
      const apps = await getCastingApplications(casting.id);
      setApplications(apps);
    }
  };

  const handleApply = async () => {
    if (!selectedCasting || !userId) return;
    setApplying(true);
    const result = await applyCasting(selectedCasting.id, userId, applyNote);
    if (result) {
      dispatch({ type: 'ADD_TOAST', payload: { type: 'success', message: 'Application submitted!', icon: '✓' } });
      setApplyNote('');
      setSelectedCasting(null);
    }
    setApplying(false);
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm('Delete this post?')) return;
    setDeletingPost(postId);
    const { deletePost } = await import('./db');
    await deletePost(postId);
    if (loadRealPosts) await loadRealPosts();
    setDeletingPost(null);
    setOpenMenu(null);
  };
  const handlePost = async () => {
    if (!form.title.trim() || !userId) return;
    setPosting(true);
    const result = await postCasting(userId, form);
    if (result) {
      setCastings(prev => [{ ...result, poster: state.user }, ...prev]);
      setShowPost(false);
      setForm({ title:'', description:'', type:'paid', location:'', rate:'', spots:1, requireSelfTape:false, sideTitle:'', maxDuration:120, slateRequired:true, tapeFormat:'Horizontal (16:9)', tapeNotes:'' });
      dispatch({ type: 'ADD_TOAST', payload: { type: 'success', message: 'Casting posted!', icon: '✓' } });
    }
    setPosting(false);
  };

  const handleShortlist = async (appId, current) => {
    await shortlistApplicant(appId, !current);
    setApplications(prev => prev.map(a => a.id === appId ? { ...a, shortlisted: !current } : a));
  };

  const userScore = verificationScore || state.user?.verificationScore || 0;

  return (
    <div style={{ maxWidth: 800, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20 }}>
        <div>
          <h2 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:28, fontWeight:700, color:T.gold, margin:0 }}>Casting Board</h2>
          <p style={{ fontSize:13, color:T.text2, margin:'4px 0 0' }}>Open calls for creative professionals</p>
        </div>
        <button
          onClick={() => setShowPost(true)}
          style={{ background:`linear-gradient(135deg,${T.gold},${T.gold2})`, border:'none', borderRadius:8, padding:'0.6rem 1.2rem', fontSize:13, fontWeight:700, color:T.bg, cursor:'pointer', fontFamily:'inherit' }}
        >
          + Post Casting
        </button>
      </div>

      {/* Post Casting Modal */}
      {showPost && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.85)', zIndex:2000, display:'flex', alignItems:'center', justifyContent:'center', padding:'1rem' }}>
          <div style={{ background:T.s1, border:`1px solid ${T.border2}`, borderRadius:16, width:'100%', maxWidth:520, padding:'1.5rem' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
              <h3 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:22, color:T.gold, margin:0 }}>Post a Casting Call</h3>
              <button onClick={() => setShowPost(false)} style={{ background:'none', border:'none', color:T.text2, fontSize:20, cursor:'pointer' }}>✕</button>
            </div>
            <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
              <input placeholder="Title (e.g. Lead Actor for Short Film)" value={form.title} onChange={e=>setForm({...form,title:e.target.value})} style={{ ...S.input }} />
              <textarea placeholder="Description — what are you looking for?" value={form.description} onChange={e=>setForm({...form,description:e.target.value})} rows={3} style={{ ...S.input, resize:'vertical', minHeight:80 }} />
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                <select value={form.type} onChange={e=>setForm({...form,type:e.target.value})} style={{ ...S.input }}>
                  <option value="paid">Paid</option>
                  <option value="unpaid">Unpaid</option>
                  <option value="deferred">Deferred Pay</option>
                  <option value="student">Student Project</option>
                </select>
                <input placeholder="Location (or Remote)" value={form.location} onChange={e=>setForm({...form,location:e.target.value})} style={{ ...S.input }} />
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                <input placeholder="Rate (e.g. $500/day)" value={form.rate} onChange={e=>setForm({...form,rate:e.target.value})} style={{ ...S.input }} />
                <input placeholder="# of spots" type="number" min={1} value={form.spots} onChange={e=>setForm({...form,spots:parseInt(e.target.value)||1})} style={{ ...S.input }} />
              </div>
              {/* Self-Tape Requirements */}
              <div style={{ borderTop:`1px solid ${T.border}`, paddingTop:12, marginTop:4 }}>
                <label style={{ display:'flex', alignItems:'center', gap:10, cursor:'pointer', marginBottom: form.requireSelfTape ? 12 : 0 }}>
                  <input type="checkbox" checked={!!form.requireSelfTape} onChange={e=>setForm({...form,requireSelfTape:e.target.checked})} style={{ accentColor:T.gold, width:16, height:16 }}/>
                  <span style={{ fontSize:13, color:T.cream, fontWeight:600 }}>Require Self-Tape Submission</span>
                </label>
                {form.requireSelfTape && (
                  <div style={{ display:'flex', flexDirection:'column', gap:10, padding:'12px', background:'rgba(212,168,83,0.06)', borderRadius:10, border:`1px solid ${T.gold}30` }}>
                    <div style={{ fontSize:10, color:T.gold, fontWeight:700, textTransform:'uppercase', letterSpacing:2 }}>Self-Tape Requirements</div>
                    <input placeholder="Sides title (e.g. Scene 12 — The Confrontation)" value={form.sideTitle} onChange={e=>setForm({...form,sideTitle:e.target.value})} style={{ ...S.input }} />
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                      <div>
                        <div style={{ fontSize:10, color:T.text3, marginBottom:4 }}>Max Duration (seconds)</div>
                        <input type="number" placeholder="120" value={form.maxDuration} onChange={e=>setForm({...form,maxDuration:parseInt(e.target.value)||120})} style={{ ...S.input }} />
                      </div>
                      <div>
                        <div style={{ fontSize:10, color:T.text3, marginBottom:4 }}>Format</div>
                        <select value={form.tapeFormat} onChange={e=>setForm({...form,tapeFormat:e.target.value})} style={{ ...S.input }}>
                          {['Horizontal (16:9)','Vertical (9:16)','Square (1:1)'].map(f=><option key={f}>{f}</option>)}
                        </select>
                      </div>
                    </div>
                    <label style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer' }}>
                      <input type="checkbox" checked={!!form.slateRequired} onChange={e=>setForm({...form,slateRequired:e.target.checked})} style={{ accentColor:T.gold }}/>
                      <span style={{ fontSize:12, color:T.text2 }}>Slate required (name, role, project)</span>
                    </label>
                    <textarea placeholder="Additional notes for the self-tape..." value={form.tapeNotes} onChange={e=>setForm({...form,tapeNotes:e.target.value})} rows={2} style={{ ...S.input, resize:'vertical' }}/>
                  </div>
                )}
              </div>
              <div style={{ display:'flex', gap:10, marginTop:4 }}>
                <button onClick={() => setShowPost(false)} style={{ flex:1, ...S.btn, ...S.btnGhost }}>Cancel</button>
                <button onClick={handlePost} style={{ flex:2, ...S.btn, ...S.btnGold }}>
                  {posting ? 'Posting...' : 'Post Casting ✦'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Apply Modal */}
      {selectedCasting && selectedCasting.posted_by !== userId && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.85)', zIndex:2000, display:'flex', alignItems:'center', justifyContent:'center', padding:'1rem' }}>
          <div style={{ background:T.s1, border:`1px solid ${T.border2}`, borderRadius:16, width:'100%', maxWidth:480, padding:'1.5rem' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
              <h3 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:20, color:T.gold, margin:0 }}>{selectedCasting.title}</h3>
              <button onClick={() => setSelectedCasting(null)} style={{ background:'none', border:'none', color:T.text2, fontSize:20, cursor:'pointer' }}>✕</button>
            </div>
            <p style={{ fontSize:13, color:T.text2, marginBottom:16, lineHeight:1.6 }}>{selectedCasting.description}</p>
            {userScore < (selectedCasting?.min_verification_score || 60)
              ? <div style={{ background:`${T.error}15`, border:`1px solid ${T.error}`, borderRadius:8, padding:'0.75rem', fontSize:13, color:T.error, marginBottom:12 }}>
                  Your verification score ({userScore}) is below {selectedCasting?.min_verification_score || 60}. Reach that score to apply to this casting.
                </div>
              : <>
                  {/* Agent/Manager — submit on behalf of roster talent */}
                  {(state.user?.account_type === 'agent' || state.user?.account_type === 'manager') && (
                    <div style={{ marginBottom:12 }}>
                      <div style={{ fontSize:10, color:T.gold, fontWeight:700, textTransform:'uppercase', letterSpacing:2, marginBottom:6 }}>Submitting As</div>
                      <select
                        value={submitOnBehalfOf || 'self'}
                        onChange={e => setSubmitOnBehalfOf(e.target.value === 'self' ? null : e.target.value)}
                        style={{ ...S.input }}
                      >
                        <option value="self">Myself</option>
                        {(roster || []).map(t => (
                          <option key={t.id} value={t.talent_id || t.id}>{t.name || t.handle} ({t.commission_rate}% commission)</option>
                        ))}
                      </select>
                      {submitOnBehalfOf && (
                        <div style={{ fontSize:11, color:T.text2, marginTop:4 }}>
                          Submitting on behalf of talent. Your commission will be tracked automatically.
                        </div>
                      )}
                    </div>
                  )}
                  <textarea
                    placeholder="Add a note to your application (optional)..."
                    value={applyNote}
                    onChange={e => setApplyNote(e.target.value)}
                    rows={3}
                    style={{ ...S.input, resize:'vertical', minHeight:80, marginBottom:12, width:'100%' }}
                  />
                  {/* Self-tape requirements display */}
                  {selectedCasting?.require_self_tape && (
                    <div style={{ background:'rgba(212,168,83,0.08)', border:`1px solid ${T.gold}30`, borderRadius:10, padding:12, marginBottom:12 }}>
                      <div style={{ fontSize:11, fontWeight:700, color:T.gold, marginBottom:6 }}>📋 Self-Tape Required</div>
                      <div style={{ fontSize:12, color:T.text2, lineHeight:1.7 }}>
                        {selectedCasting.side_title && <div>Sides: <strong style={{color:T.cream}}>{selectedCasting.side_title}</strong></div>}
                        {selectedCasting.max_duration && <div>Max duration: <strong style={{color:T.cream}}>{Math.floor(selectedCasting.max_duration/60)}:{String(selectedCasting.max_duration%60).padStart(2,'0')}</strong></div>}
                        {selectedCasting.slate_required && <div>✓ Slate required</div>}
                        {selectedCasting.tape_format && <div>Format: <strong style={{color:T.cream}}>{selectedCasting.tape_format}</strong></div>}
                        {selectedCasting.tape_notes && <div style={{marginTop:4}}>{selectedCasting.tape_notes}</div>}
                      </div>
                      <button
                        onClick={() => { setSelectedCasting(null); dispatch({ type:'SET_VIEW', payload:'selftape' }); }}
                        style={{ marginTop:8, background:`linear-gradient(135deg,${T.gold},${T.gold2})`, border:'none', borderRadius:8, padding:'6px 14px', fontSize:12, fontWeight:700, color:T.bg, cursor:'pointer', fontFamily:'inherit' }}
                      >
                        Record Self-Tape →
                      </button>
                    </div>
                  )}
                  <div style={{ display:'flex', gap:10 }}>
                    <button onClick={() => setSelectedCasting(null)} style={{ flex:1, ...S.btn, ...S.btnGhost }}>Cancel</button>
                    <button onClick={handleApply} disabled={applying} style={{ flex:2, ...S.btn, ...S.btnGold, opacity:applying?0.6:1 }}>
                      {applying ? 'Submitting...' : 'Submit Application ✦'}
                    </button>
                  </div>
                </>
            }
          </div>
        </div>
      )}

      {/* Applications Panel (for casting owner) */}
      {selectedCasting && selectedCasting.posted_by === userId && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.85)', zIndex:2000, display:'flex', alignItems:'center', justifyContent:'center', padding:'1rem' }}>
          <div style={{ background:T.s1, border:`1px solid ${T.border2}`, borderRadius:16, width:'100%', maxWidth:560, padding:'1.5rem', maxHeight:'80vh', display:'flex', flexDirection:'column' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
              <h3 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:20, color:T.gold, margin:0 }}>Applicants — {selectedCasting.title}</h3>
              <button onClick={() => setSelectedCasting(null)} style={{ background:'none', border:'none', color:T.text2, fontSize:20, cursor:'pointer' }}>✕</button>
            </div>
            <div style={{ flex:1, overflowY:'auto' }}>
              {applications.length === 0
                ? <p style={{ color:T.text3, fontSize:13, textAlign:'center', padding:'2rem 0' }}>No applications yet.</p>
                : applications.map(app => (
                    <div key={app.id} style={{ display:'flex', alignItems:'center', gap:12, padding:'0.75rem 0', borderBottom:`1px solid ${T.border}` }}>
                      {app.applicant?.avatar_url
                        ? <img src={app.applicant.avatar_url} alt="avatar" style={{ width:40, height:40, borderRadius:'50%', objectFit:'cover' }} />
                        : <Avatar emoji="👤" size={40} />
                      }
                      <div style={{ flex:1 }}>
                        <div style={{ fontWeight:600, fontSize:14, color:T.cream }}>{app.applicant?.display_name}</div>
                        <div style={{ fontSize:12, color:T.text2 }}>{app.applicant?.handle} · {(app.applicant?.crafts||[]).slice(0,3).join(', ')}</div>
                        {app.note && <div style={{ fontSize:12, color:T.text3, marginTop:4, fontStyle:'italic' }}>"{app.note}"</div>}
                      </div>
                      <button
                        onClick={() => handleShortlist(app.id, app.shortlisted)}
                        style={{ background: app.shortlisted ? `${T.gold}20` : T.s3, border:`1px solid ${app.shortlisted ? T.gold : T.border2}`, borderRadius:8, padding:'4px 10px', fontSize:12, fontWeight:600, color: app.shortlisted ? T.gold : T.text2, cursor:'pointer', fontFamily:'inherit' }}
                      >
                        {app.shortlisted ? '★ Shortlisted' : '☆ Shortlist'}
                      </button>
                    </div>
                  ))
              }
            </div>
          </div>
        </div>
      )}

      {/* Casting List */}
      {loading
        ? <div style={{ textAlign:'center', padding:'3rem', color:T.text3 }}>Loading castings...</div>
        : castings.length === 0
          ? <div style={{ ...S.card, textAlign:'center', padding:'3rem', color:T.text3 }}>
              <p style={{ fontSize:16, marginBottom:8 }}>No open castings yet.</p>
              <p style={{ fontSize:13 }}>Be the first to post a casting call.</p>
            </div>
          : castings.map(casting => (
              <div key={casting.id} style={{ ...S.card, marginBottom:16, cursor:'pointer' }} onClick={() => handleSelectCasting(casting)}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:10 }}>
                  <div style={{ flex:1 }}>
                    <h3 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:20, fontWeight:700, color:T.cream, margin:'0 0 4px' }}>{casting.title}</h3>
                    <div style={{ fontSize:12, color:T.text2, display:'flex', gap:12, flexWrap:'wrap' }}>
                      {casting.location && <span>📍 {casting.location}</span>}
                      {casting.rate && <span>💰 {casting.rate}</span>}
                      <span>👥 {casting.spots} spot{casting.spots !== 1 ? 's' : ''}</span>
                    </div>
                  </div>
                  <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-end', gap:6 }}>
                    <span style={{ background: casting.type === 'paid' ? `${T.gold}20` : `${T.teal}20`, border:`1px solid ${casting.type === 'paid' ? T.gold : T.teal}`, borderRadius:20, padding:'2px 10px', fontSize:11, fontWeight:700, color: casting.type === 'paid' ? T.gold : T.teal, textTransform:'uppercase' }}>
                      {casting.type}
                    </span>
                    {casting.posted_by === userId && <span style={{ fontSize:11, color:T.text3 }}>Your casting</span>}
                  </div>
                </div>
                {casting.description && <p style={{ fontSize:13, color:T.text2, lineHeight:1.6, marginBottom:10 }}>{casting.description.slice(0,150)}{casting.description.length > 150 ? '...' : ''}</p>}
                <div style={{ display:'flex', alignItems:'center', gap:8, fontSize:12, color:T.text3 }}>
                  {casting.poster?.avatar_url
                    ? <img src={casting.poster.avatar_url} alt="poster" style={{ width:20, height:20, borderRadius:'50%', objectFit:'cover' }} />
                    : null
                  }
                  <span>Posted by {casting.poster?.display_name || 'Unknown'} · {new Date(casting.created_at).toLocaleDateString()}</span>
                  <span style={{ marginLeft:'auto', color: casting.min_verification_score <= (userScore) ? T.teal : T.error }}>
                    Score {casting.min_verification_score}+ required
                  </span>
                </div>
              </div>
            ))
      }
    </div>
  );
}

// ── CONNECTIONS VIEW ──
function ConnectionsView({ state, dispatch, realConnections, loadRealConnections, userId }) {
  const [tab, setTab] = useState('all');
  const [suggested, setSuggested] = useState([]);
  useEffect(() => { if (userId) getSuggestedConnections(userId).then(setSuggested); }, [userId]);

  const tabs = [
    { id: 'all', label: 'All' },
    { id: 'connected', label: 'Connected' },
    { id: 'pending', label: 'Pending' },
    { id: 'suggested', label: 'Suggested' },
  ];

  const suggestedMapped = suggested.map(p => ({ id: p.id, name: p.display_name || p.handle, av: p.avatar_url || null, crafts: p.crafts || [], mutual: 0, status: 'suggested', connectionId: null }));
  const realMapped = (realConnections || []).map(c => ({
        id: c.id,
        name: c.requester_id === userId
          ? (c.addressee?.display_name || c.addressee?.handle || 'Unknown')
          : (c.requester?.display_name || c.requester?.handle || 'Unknown'),
        av: c.requester_id === userId ? (c.addressee?.avatar_url || null) : (c.requester?.avatar_url || null),
        crafts: c.requester_id === userId ? (c.addressee?.crafts || []) : (c.requester?.crafts || []),
        mutual: 0,
        status: c.status,
        connectionId: c.id,
      }));
  const allConnections = [...realMapped, ...suggestedMapped];
  const filtered = allConnections.filter(c => tab === 'all' || c.status === tab);

  const statusConfig = {
    connected: { label: 'Connected', color: T.success },
    pending: { label: 'Pending', color: T.warn },
    suggested: { label: 'Suggested', color: T.text2 },
  };

  return (
    <div className="connections-container" style={{ maxWidth: 640, margin: '0 auto' }}>
      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 20, background: T.s2, borderRadius: 10, padding: 4 }}>
        {tabs.map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            style={{
              ...S.btn, flex: 1, justifyContent: 'center', borderRadius: 7,
              background: tab === t.id ? T.s4 : 'transparent',
              color: tab === t.id ? T.text : T.text2, border: 'none',
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {filtered.length === 0 && (
          <div style={{ textAlign:'center', padding:'4rem 2rem', color:T.text3 }}>
            <div style={{ fontSize:36, marginBottom:12 }}>✦</div>
            <div style={{ fontSize:15, fontWeight:600, color:T.text2, marginBottom:6 }}>No connections yet</div>
            <div style={{ fontSize:13 }}>Browse talent and start building your network.</div>
          </div>
        )}
        {filtered.map(conn => {
          const { label, color } = statusConfig[conn.status];
          return (
            <div key={conn.id} style={{ ...S.card, display: 'flex', alignItems: 'center', gap: 12 }}>
              {conn.av && typeof conn.av === 'string' && conn.av.startsWith('data') ? <img src={conn.av} alt="avatar" style={{width:40,height:40,borderRadius:'50%',objectFit:'cover'}} /> : <Avatar emoji="👤" />}
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 2 }}>{conn.name}</div>
                <div style={{ fontSize: 12, color: T.text2 }}>
                  {conn.crafts.join(', ')} · {conn.mutualConnections} mutual connections
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                {conn.status === 'suggested' && (
                  <>
                    <button style={{ ...S.btn, ...S.btnGhost, padding: '6px 12px' }} onClick={() => setSuggested(prev => prev.filter(p => p.id !== conn.id))}>Ignore</button>
                    <button
                      style={{ ...S.btn, ...S.btnGold, padding: '6px 12px' }}
                      onClick={async () => {
                        if (userId) {
                          await sendConnectionRequest(userId, conn.id);
                          await createNotification(conn.id, 'connection', 'New Connection Request', `${state.user.name} wants to connect with you`, userId, null);
                          if (loadRealConnections) await loadRealConnections();
                        }
                        dispatch({ type: 'ADD_TOAST', payload: { type: 'success', message: `Connection request sent to ${conn.name}`, icon: '✦' } });
                      }}
                    >
                      Connect
                    </button>
                  </>
                )}
                {conn.status === 'pending' && (
                  <>
                    <button style={{ ...S.btn, ...S.btnGhost, padding: '6px 12px' }} onClick={async () => { if (conn.connectionId) { await updateConnectionStatus(conn.connectionId, 'declined'); if (loadRealConnections) await loadRealConnections(); } dispatch({ type: 'ADD_TOAST', payload: { type: 'info', message: 'Connection declined', icon: '✕' } }); }}>Decline</button>
                    <button
                      style={{ ...S.btn, ...S.btnGold, padding: '6px 12px' }}
                      onClick={async () => {
                        if (conn.connectionId) {
                          await updateConnectionStatus(conn.connectionId, 'accepted');
                          if (loadRealConnections) await loadRealConnections();
                        }
                        dispatch({ type: 'ADD_TOAST', payload: { type: 'success', message: 'Connection accepted!', icon: '✦' } });
                      }}
                    >
                      Accept
                    </button>
                  </>
                )}
                {conn.status === 'connected' && (
                  <Badge color={color} bg={`${color}15`}>
                    <Check size={10} /> {label}
                  </Badge>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── MESSAGES VIEW ──
function MessagesView({ state, dispatch, userId, realProfiles }) {
  const windowWidth = useWindowWidth();
  const isMobile = windowWidth < 768;
  const [realMessages, setRealMessages] = useState([]);
  const [realThreads, setRealThreads] = useState([]);
  const [activeParticipantId, setActiveParticipantId] = useState(null);
  const [showCompose, setShowCompose] = useState(false);
  const [composeSearch, setComposeSearch] = useState('');
  const [localProfiles, setLocalProfiles] = useState([]);
  useEffect(() => { if (userId) getThreads(userId).then(setRealThreads); }, [userId]);
  useEffect(() => { getAllProfiles().then(setLocalProfiles); }, []);

  const loadMessages = async (otherId) => {
    if (!userId || !otherId) return;
    const data = await getMessages(userId, otherId);
    setRealMessages(data);
  };

  const handleOpenThread = async (threadId, participantId) => {
    dispatch({ type: 'SET_THREAD', payload: threadId });
    if (participantId) {
      setActiveParticipantId(participantId);
      await loadMessages(participantId);
    }
  };

  // eslint-disable-next-line no-unused-vars
  const handleSendReal = async () => {
    const inp = document.getElementById('chatInput');
    const txt = inp?.value?.trim();
    if (!txt || !userId || !activeParticipantId) return;
    inp.value = '';
    await sendMessage(userId, activeParticipantId, txt);
    await loadMessages(activeParticipantId);
  };
  const { activeThread, chatMessages } = state;
  const [msgInput, setMsgInput] = useState('');
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [activeThread, chatMessages]);

  const handleSend = async () => {
    if (!msgInput.trim() || !activeThread) return;
    if (userId && activeParticipantId) {
      await sendMessage(userId, activeParticipantId, msgInput);
      await createNotification(activeParticipantId, 'message', 'New Message', `${state.user?.name || 'Someone'} sent you a message`, userId, null);
      await loadMessages(activeParticipantId);
      getThreads(userId).then(setRealThreads);
    } else {
      dispatch({ type: 'SEND_MESSAGE', payload: { threadId: activeThread, text: msgInput } });
    }
    setMsgInput('');
  };

  const activeMessages = realMessages.length > 0
    ? realMessages.map(m => ({ id: m.id, from: m.sender_id === userId ? 'me' : 'them', text: m.content, me: m.sender_id === userId }))
    : activeThread ? (chatMessages[activeThread] || []) : [];
  const activeParticipant = realThreads.find(t => t.threadId === activeThread) || (activeThread === activeParticipantId ? { participant: localProfiles.find(p => p.id === activeParticipantId)?.display_name || 'User', avatar: '👤' } : null);

  const showThreadList = !isMobile || !activeThread;
  const showChatPanel = !isMobile || !!activeThread;
  return (
    <div className="chat-layout" style={{ display: 'flex', height: isMobile ? 'calc(100vh - 180px)' : 'calc(100vh - 120px)', gap: 16 }}>
      {/* Thread list */}
      {showThreadList && <div style={{ ...S.card, width: isMobile ? '100%' : 280, flexShrink: 0, overflow: 'auto', padding: 0 }}>
        <div style={{ padding: '0.75rem 1rem', borderBottom: `1px solid ${T.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontWeight: 600, fontSize: 13, color: T.text2 }}>Messages</span>
          <button onClick={() => setShowCompose(true)} style={{ background: `${T.gold}20`, border: `1px solid ${T.gold}`, borderRadius: 8, padding: '4px 10px', fontSize: 12, fontWeight: 700, color: T.gold, cursor: 'pointer', fontFamily: 'inherit' }}>+ New</button>
        </div>
        {showCompose && (
          <div style={{ padding: '0.75rem 1rem', borderBottom: `1px solid ${T.border}`, background: T.s2 }}>
            <input
              autoFocus
              placeholder="Search by name or @handle..."
              value={composeSearch}
              onChange={e => setComposeSearch(e.target.value)}
              style={{ background: T.s3, border: `1px solid ${T.border2}`, borderRadius: 8, padding: '0.5rem 0.75rem', color: T.cream, fontSize: 13, width: '100%', outline: 'none', fontFamily: 'inherit' }}
            />
            {composeSearch.trim().length > 0 && (
              <div style={{ marginTop: 6, maxHeight: 180, overflowY: 'auto' }}>
                {(localProfiles.length > 0 ? localProfiles : (realProfiles || []))
                  .filter(p => p.id !== userId && (
                    (p.display_name || '').toLowerCase().includes(composeSearch.toLowerCase()) ||
                    (p.handle || '').toLowerCase().includes(composeSearch.toLowerCase())
                  ))
                  .slice(0, 8)
                  .map(p => (
                    <div
                      key={p.id}
                      onClick={() => {
                        handleOpenThread(p.id, p.id);
                        setShowCompose(false);
                        setComposeSearch('');
                      }}
                      style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '0.5rem 0.25rem', cursor: 'pointer', borderRadius: 8, transition: '0.15s' }}
                    >
                      {p.avatar_url
                        ? <img src={p.avatar_url} alt="avatar" style={{ width: 30, height: 30, borderRadius: '50%', objectFit: 'cover', border: `1px solid ${T.border2}` }} />
                        : <Avatar emoji="👤" size={30} />
                      }
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: T.cream }}>{p.display_name}</div>
                        <div style={{ fontSize: 11, color: T.text2 }}>{p.handle}</div>
                      </div>
                    </div>
                  ))
                }
              </div>
            )}
            <button onClick={() => { setShowCompose(false); setComposeSearch(''); }} style={{ marginTop: 6, background: 'none', border: 'none', color: T.text3, fontSize: 12, cursor: 'pointer', fontFamily: 'inherit' }}>Cancel</button>
          </div>
        )}
        {realThreads.map(thread => (
          <div
            key={thread.threadId}
            onClick={() => handleOpenThread(thread.threadId, thread.participantId || null)}
            style={{
              display: 'flex', alignItems: 'center', gap: 12,
              padding: '0.9rem 1.25rem', cursor: 'pointer',
              background: activeThread === thread.threadId ? T.s3 : 'transparent',
              borderBottom: `1px solid ${T.border}`,
              transition: 'background 0.15s',
            }}
          >
            {thread.avatarUrl ? <img src={thread.avatarUrl} alt="avatar" style={{width:38,height:38,borderRadius:'50%',objectFit:'cover',border:`2px solid ${T.border2}`}} /> : <Avatar emoji={thread.avatar} />}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 2 }}>{thread.participant}</div>
              <div style={{ fontSize: 12, color: T.text2, whiteSpace: 'nowrap' }}>
                {thread.preview}
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
              <span style={{ fontSize: 11, color: T.text3 }}>{thread.time}</span>
              {thread.unread > 0 && (
                <span style={{ background: T.gold, color: T.bg, borderRadius: 20, fontSize: 10, fontWeight: 700, padding: '1px 6px' }}>
                  {thread.unread}
                </span>
              )}
            </div>
          </div>
        ))}
      </div>}
      {showChatPanel && <div style={{ ...S.card, flex: 1, display: 'flex', flexDirection: 'column', padding: 0 }}>
        {activeParticipant ? (
          <>
            <div style={{ padding: '1rem 1.25rem', borderBottom: `1px solid ${T.border}`, display: 'flex', alignItems: 'center', gap: 12 }}>
              {isMobile && <button onClick={() => dispatch({ type: 'SET_ACTIVE_THREAD', payload: null })} style={{ background:'none', border:'none', color:T.text2, cursor:'pointer', padding:'0 4px', display:'flex', alignItems:'center' }}><ArrowLeft size={18} /></button>}
              <Avatar emoji={activeParticipant.avatar} />
              <div style={{ fontWeight: 600 }}>{activeParticipant.participant}</div>
            </div>
            <div style={{ flex: 1, overflow: 'auto', padding: '1.25rem', display: 'flex', flexDirection: 'column', gap: 10 }}>
              {activeMessages.map(msg => (
                <div key={msg.id} style={{ display: 'flex', justifyContent: msg.me ? 'flex-end' : 'flex-start' }}>
                  <div style={{
                    background: msg.me ? G.gold : T.s3,
                    color: msg.me ? T.bg : T.text,
                    borderRadius: msg.me ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                    padding: '0.6rem 1rem', fontSize: 14, maxWidth: '70%',
                  }}>
                    {msg.text}
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>
            <div style={{ padding: '0.75rem 1.25rem', borderTop: `1px solid ${T.border}`, display: 'flex', gap: 10 }}>
              <input
                value={msgInput}
                onChange={e => setMsgInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleSend()}
                placeholder="Type a message..."
                style={{ ...S.input }}
              />
              <button style={{ ...S.btn, ...S.btnGold, padding: '0.6rem 1rem' }} onClick={handleSend}>
                <Send size={15} />
              </button>
            </div>
          </>
        ) : (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 10, color: T.text3 }}>
            <MessageCircle size={40} strokeWidth={1} />
            <p style={{ fontSize: 14 }}>Select a conversation</p>
          </div>
        )}
      </div>}
    </div>
  );
}

// ── ANALYTICS VIEW ──
function AnalyticsView({ state, realVerification, realStats, userId, loadRealVerification }) {
  const verSignals = realVerification ? {
    email: realVerification.email_verified || false,
    phone: realVerification.phone_verified || false,
    socialProofLinks: (realVerification.social_proof_links || []).length,
    portfolioUpload: realVerification.portfolio_uploaded || false,
    credentialUpload: realVerification.credential_uploaded || false,
    peerCoSignatures: realVerification.peer_co_signatures || 0,
    venueCoSignature: realVerification.venue_co_signature || false,
    industryProEndorsement: realVerification.industry_pro_endorsement || false,
  } : state.user.verificationSignals;

  // eslint-disable-next-line no-unused-vars
  const toggleSignal = async (field) => {
    if (!userId) return;
    const current = realVerification || {};
    const updates = { [field]: !current[field] };
    await upsertVerificationSignals(userId, updates);
    if (loadRealVerification) await loadRealVerification();
    // loadRealStats handled by App.jsx
  };
  const verScore = computeVerificationScore(verSignals);
  const craftScore = computeCraftLineScore(state.user.craftLineDimensions);
  const gate = getVerificationGate(verScore);

  const stats = [
    { label: 'Profile Views', value: (realStats?.profileViews ?? '—').toString(), delta: 'total', color: T.gold },
    { label: 'Connections', value: (realStats?.connectionRequests ?? '—').toString(), delta: 'total', color: T.teal },
    { label: 'Casting Applications', value: (realStats?.castingApplications ?? '—').toString(), delta: 'total', color: T.purple },
    { label: 'Casting Shortlists', value: (realStats?.castingShortlists ?? '—').toString(), delta: 'total', color: T.success },
  ];

  const craftDimensions = [
    { key: 'craftQuality', label: 'Craft Quality', weight: '30%' },
    { key: 'networkDepth', label: 'Network Depth', weight: '20%' },
    { key: 'activityConsistency', label: 'Activity Consistency', weight: '18%' },
    { key: 'collaborationHistory', label: 'Collaboration History', weight: '14%' },
    { key: 'endorsementQuality', label: 'Endorsement Quality', weight: '10%' },
    { key: 'profileCompleteness', label: 'Profile Completeness', weight: '8%' },
  ];

  const verSignalList = [
    { key: 'email_verified', label: 'Email Verified', pts: 5, done: verSignals.email },
    { key: 'phone_verified', label: 'Phone Verified', pts: 5, done: verSignals.phone },
    { key: 'socialProofLinks', label: 'Social Proof Links', pts: 16, done: verSignals.socialProofLinks > 0 },
    { key: 'portfolio_uploaded', label: 'Portfolio Uploaded', pts: 10, done: verSignals.portfolioUpload },
    { key: 'credential_uploaded', label: 'Credential Uploaded', pts: 12, done: verSignals.credentialUpload },
    { key: 'peerCoSignatures', label: 'Peer Co-Signatures', pts: 8, done: verSignals.peerCoSignatures > 0 },
    { key: 'venueCoSignature', label: 'Venue Co-Signature', pts: 15, done: verSignals.venueCoSignature },
    { key: 'industryProEndorsement', label: 'Industry Pro Endorsement', pts: 13, done: verSignals.industryProEndorsement },
  ];

  return (
    <div className="analytics-container" style={{ maxWidth: 900, margin: '0 auto' }}>
      {/* Stats grid */}
      <div className="analytics-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 14, marginBottom: 24 }}>
        {stats.map(stat => (
          <div key={stat.label} style={{ ...S.card }}>
            <div style={{ fontSize: 11, color: T.text3, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.08em' }}>{stat.label}</div>
            <div style={{ fontSize: 28, fontWeight: 700, fontFamily: 'Cormorant Garamond, serif', color: stat.color }}>{stat.value}</div>
            <div style={{ fontSize: 12, color: stat.color, marginTop: 4 }}>{stat.delta} this month</div>
          </div>
        ))}
      </div>

      <div className="analytics-scores-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
        {/* Craft Line Score */}
        <div style={S.card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
            <div>
              <div style={{ fontSize: 12, color: T.text3, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Craft Line Score</div>
              <div style={{ fontSize: 44, fontWeight: 700, fontFamily: 'Cormorant Garamond, serif', color: T.gold, lineHeight: 1 }}>{craftScore}</div>
              <div style={{ fontSize: 12, color: T.text2, marginTop: 4 }}>out of 100</div>
            </div>
            <CraftLineScorePill score={craftScore} />
          </div>
          {craftDimensions.map(dim => (
            <div key={dim.key} style={{ marginBottom: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
                <span style={{ color: T.text2 }}>{dim.label}</span>
                <span style={{ color: T.text3, fontFamily: 'DM Mono, monospace' }}>
                  {state.user.craftLineDimensions[dim.key]} · {dim.weight}
                </span>
              </div>
              <ScoreBar value={state.user.craftLineDimensions[dim.key]} color={T.gold} height={5} />
            </div>
          ))}
        </div>

        {/* Verification Score */}
        <div style={S.card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
            <div>
              <div style={{ fontSize: 12, color: T.text3, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Verification Score</div>
              <div style={{ fontSize: 44, fontWeight: 700, fontFamily: 'Cormorant Garamond, serif', color: T.teal, lineHeight: 1 }}>{verScore}</div>
              <div style={{ fontSize: 12, color: T.text2, marginTop: 4 }}>Gate {gate} of 3</div>
            </div>
            <VerificationBadge score={verScore} />
          </div>
          {verSignalList.map(sig => (
            <div key={sig.key} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
              <div style={{
                width: 18, height: 18, borderRadius: '50%', flexShrink: 0,
                background: sig.done ? T.teal : T.s4,
                border: `1px solid ${sig.done ? T.teal : T.border2}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                {sig.done && <Check size={10} color={T.bg} strokeWidth={3} />}
              </div>
              <div style={{ flex: 1, fontSize: 13, color: sig.done ? T.text : T.text2 }}>{sig.label}</div>
              <div style={{ fontSize: 11, fontFamily: 'DM Mono, monospace', color: sig.done ? T.teal : T.text3 }}>+{sig.pts}pts</div>
            </div>
          ))}
          <div style={{ marginTop: 12, paddingTop: 12, borderTop: `1px solid ${T.border}`, fontSize: 12, color: T.text2 }}>
            ✦ Verification is always free. Complete remaining signals to unlock full platform access.
          </div>
        </div>
      </div>
    </div>
  );
}

// ── SETTINGS VIEW ──
function SettingsView({ state, dispatch }) {
  const [activeTab, setActiveTab] = useState('notifications');

  const settingsTabs = [
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'account', label: 'Account', icon: Settings },
    { id: 'privacy', label: 'Privacy', icon: Shield },
    { id: 'ratecards', label: 'Rate Card', icon: FileText },
    { id: 'legal', label: 'Legal', icon: FileText },
    { id: 'subscription', label: 'Subscription', icon: Star },
  ];

  const [rateItems, setRateItems] = React.useState(() => {
    const saved = localStorage.getItem('rate_card_' + state.user?.id);
    return saved ? JSON.parse(saved) : [
      { id: 1, service: 'Photography — Portrait Session', rate: '', unit: 'per hour', public: true },
      { id: 2, service: 'Photography — Headshots', rate: '', unit: 'flat rate', public: true },
      { id: 3, service: 'Runway / Editorial', rate: '', unit: 'per hour', public: true },
    ];
  });
  const [ratePublic, setRatePublic] = React.useState(() => {
    const saved = localStorage.getItem('rate_card_public_' + state.user?.id);
    return saved === 'true';
  });

  const saveRateCard = (items, pub) => {
    localStorage.setItem('rate_card_' + state.user?.id, JSON.stringify(items));
    localStorage.setItem('rate_card_public_' + state.user?.id, String(pub));
    setRateItems(items);
    setRatePublic(pub);
  };

  const notifGroups = [
    {
      title: 'Activity',
      items: [
        { key: 'connectionRequests', label: 'Connection requests' },
        { key: 'castingShortlists', label: 'Casting shortlists' },
        { key: 'jobMatches', label: 'Job matches' },
        { key: 'messageReceipts', label: 'Message read receipts' },
      ],
    },
    {
      title: 'Platform',
      items: [
        { key: 'verificationUpdates', label: 'Verification score updates' },
        { key: 'craftLineChanges', label: 'Craft Line score changes' },
        { key: 'platformAnnouncements', label: 'Platform announcements' },
      ],
    },
    {
      title: 'Delivery',
      items: [
        { key: 'pushEnabled', label: 'Push notifications' },
        { key: 'emailDigest', label: 'Weekly email digest' },
      ],
    },
  ];

  const subTiers = [
    { id: 'free', label: 'Free', price: '$0/mo', features: ['Basic profile', 'Limited connections', 'Read-only feed'], current: false },
    { id: 'pro', label: 'Artist Pro', price: '$9/mo ($7/mo annual)', features: ['Full Craft Line scoring', 'Direct booking', 'Analytics', 'Contract delivery', 'Casting access at score 75+'], current: false },
    { id: 'studio', label: 'Artist Studio', price: '$19/mo ($15/mo annual)', features: ['All Artist Pro features', 'Vizion (storyboarding)', 'Curtainz (post-production)', 'White-label profile', 'Team seats'], current: false },
  ];

  return (
    <div style={{ maxWidth: 720, margin: '0 auto' }}>
      <div style={{ display: 'flex', gap: 4, marginBottom: 24, background: T.s2, borderRadius: 10, padding: 4 }}>
        {settingsTabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              ...S.btn, flex: 1, justifyContent: 'center', borderRadius: 7, gap: 6,
              background: activeTab === tab.id ? T.s4 : 'transparent',
              color: activeTab === tab.id ? T.text : T.text2, border: 'none',
              fontSize: 13,
            }}
          >
            <tab.icon size={14} /> {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'notifications' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {notifGroups.map(group => (
            <div key={group.title} style={S.card}>
              <div style={{ fontSize: 12, color: T.text3, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 14 }}>{group.title}</div>
              {group.items.map((item, i) => (
                <div
                  key={item.key}
                  style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    paddingBottom: i < group.items.length - 1 ? 12 : 0,
                    marginBottom: i < group.items.length - 1 ? 12 : 0,
                    borderBottom: i < group.items.length - 1 ? `1px solid ${T.border}` : 'none',
                  }}
                >
                  <span style={{ fontSize: 14, color: T.text }}>{item.label}</span>
                  <button
                    onClick={() => dispatch({ type: 'TOGGLE_NOTIF_SETTING', payload: item.key })}
                    style={{
                      width: 44, height: 24, borderRadius: 12, position: 'relative',
                      background: state.notifSettings[item.key] ? T.gold : T.s4,
                      border: `1px solid ${state.notifSettings[item.key] ? T.gold : T.border2}`,
                      cursor: 'pointer', transition: 'all 0.2s ease',
                    }}
                  >
                    <div style={{
                      position: 'absolute', top: 3,
                      left: state.notifSettings[item.key] ? 23 : 3,
                      width: 16, height: 16, borderRadius: '50%',
                      background: state.notifSettings[item.key] ? T.bg : T.text3,
                      transition: 'left 0.2s ease',
                    }} />
                  </button>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      {activeTab === 'subscription' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {subTiers.map(tier => (
            <div key={tier.id} style={{ ...S.card, border: `1px solid ${tier.current ? T.gold : T.border}`, position: 'relative' }}>
              {tier.current && (
                <div style={{ position: 'absolute', top: 12, right: 12 }}>
                  <Badge color={T.gold} bg={`${T.gold}15`}><Check size={10} /> Current Plan</Badge>
                </div>
              )}
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 12 }}>
                <span style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 22, fontWeight: 700 }}>{tier.label}</span>
                <span style={{ fontSize: 18, fontWeight: 700, color: T.gold }}>{tier.price}</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: tier.current ? 0 : 14 }}>
                {tier.features.map(f => (
                  <div key={f} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                    <Check size={13} color={T.teal} strokeWidth={3} />
                    <span style={{ color: T.text2 }}>{f}</span>
                  </div>
                ))}
              </div>
              {!tier.current && (
                <button
                  style={{ ...S.btn, ...(tier.id === 'studio' ? S.btnGold : S.btnGhost), width: '100%', justifyContent: 'center', marginTop: 4 }}
                  onClick={() => dispatch({ type: 'ADD_TOAST', payload: { type: 'info', message: `Upgrading to ${tier.label}...`, icon: '✦' } })}
                >
                  {tier.id === 'free' ? 'Downgrade' : `Upgrade to ${tier.label}`}
                </button>
              )}
            </div>
          ))}
          <p style={{ fontSize: 12, color: T.text3, textAlign: 'center' }}>
            ✦ Verification is always free to Artisans — regardless of subscription tier.
          </p>
        </div>
      )}

      {activeTab === 'account' && (
        <div style={S.card}>
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 12, color: T.text3, display: 'block', marginBottom: 6 }}>Display Name</label>
            <input style={S.input} defaultValue={state.user.name} />
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 12, color: T.text3, display: 'block', marginBottom: 6 }}>Handle</label>
            <input style={S.input} defaultValue={state.user.handle} />
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 12, color: T.text3, display: 'block', marginBottom: 6 }}>Bio</label>
            <textarea style={{ ...S.input, minHeight: 80, resize: 'vertical' }} defaultValue={state.user.bio} />
          </div>
          <div style={{ marginBottom: 20 }}>
            <label style={{ fontSize: 12, color: T.text3, display: 'block', marginBottom: 6 }}>Location</label>
            <input style={S.input} defaultValue={state.user.location} />
          </div>
          <button style={{ ...S.btn, ...S.btnGold }}
            onClick={() => dispatch({ type: 'ADD_TOAST', payload: { type: 'success', message: 'Profile updated', icon: '✦' } })}>
            Save Changes
          </button>
        </div>
      )}

      {activeTab === 'privacy' && (
        <div style={S.card}>
          <p style={{ color: T.text2, fontSize: 14, lineHeight: 1.7, marginBottom: 16 }}>
            Privacy settings control who can see your profile, verification score, and Craft Line data.
          </p>
          {[
            { label: 'Show verification score to Industry Pros only', sub: 'Only users with Industry Pro status can see your numeric score' },
            { label: 'Show Craft Line score publicly', sub: 'Visible on your profile to all users' },
            { label: 'Allow direct booking requests', sub: 'Anyone can send you a booking request' },
            { label: 'Appear in Talent Search', sub: 'Your profile appears in search results' },
          ].map((item, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: 14, marginBottom: 14, borderBottom: i < 3 ? `1px solid ${T.border}` : 'none' }}>
              <div>
                <div style={{ fontSize: 14 }}>{item.label}</div>
                <div style={{ fontSize: 12, color: T.text3, marginTop: 2 }}>{item.sub}</div>
              </div>
              <button style={{ width: 44, height: 24, borderRadius: 12, background: T.gold, border: 'none', position: 'relative', cursor: 'pointer' }}>
                <div style={{ position: 'absolute', top: 3, left: 23, width: 16, height: 16, borderRadius: '50%', background: T.bg }} />
              </button>
            </div>
          ))}
        </div>
      )}
      {activeTab === 'ratecards' && (
        <div>
          <div style={{ ...S.card, marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 600, color: T.cream }}>Make rate card public</div>
                <div style={{ fontSize: 12, color: T.text3, marginTop: 2 }}>Show your rates on your press page and public profile</div>
              </div>
              <button onClick={() => saveRateCard(rateItems, !ratePublic)} style={{ width: 44, height: 24, borderRadius: 12, background: ratePublic ? T.gold : T.s3, border: `1px solid ${ratePublic ? T.gold : T.border2}`, position: 'relative', cursor: 'pointer' }}>
                <div style={{ position: 'absolute', top: 3, left: ratePublic ? 23 : 3, width: 16, height: 16, borderRadius: '50%', background: ratePublic ? T.bg : T.text3, transition: 'left 0.2s' }} />
              </button>
            </div>
            {ratePublic && <div style={{ fontSize: 11, color: T.teal }}>✓ Your rate card is visible on your press page</div>}
          </div>
          <div style={{ ...S.card, marginBottom: 12 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: T.gold, marginBottom: 12 }}>Your Rates</div>
            {rateItems.map((item, i) => (
              <div key={item.id} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr auto', gap: 8, marginBottom: 8, alignItems: 'center' }}>
                <input value={item.service} onChange={e => saveRateCard(rateItems.map(r => r.id === item.id ? { ...r, service: e.target.value } : r), ratePublic)} style={{ ...S.input, fontSize: 12 }} placeholder="Service name"/>
                <input value={item.rate} onChange={e => saveRateCard(rateItems.map(r => r.id === item.id ? { ...r, rate: e.target.value } : r), ratePublic)} style={{ ...S.input, fontSize: 12 }} placeholder="Rate (e.g. $150)"/>
                <select value={item.unit} onChange={e => saveRateCard(rateItems.map(r => r.id === item.id ? { ...r, unit: e.target.value } : r), ratePublic)} style={{ ...S.input, fontSize: 12, cursor: 'pointer' }}>
                  {['per hour', 'per day', 'flat rate', 'per image', 'per word', 'per track', 'negotiable'].map(u => <option key={u}>{u}</option>)}
                </select>
                <button onClick={() => saveRateCard(rateItems.filter(r => r.id !== item.id), ratePublic)} style={{ background: 'none', border: 'none', color: T.text3, cursor: 'pointer', fontSize: 16 }}>×</button>
              </div>
            ))}
            <button onClick={() => saveRateCard([...rateItems, { id: Date.now(), service: '', rate: '', unit: 'per hour', public: true }], ratePublic)} style={{ ...S.btn, ...S.btnGhost, fontSize: 12, marginTop: 4 }}>+ Add Rate</button>
          </div>
          <div style={{ fontSize: 11, color: T.text3 }}>Rates are stored locally. Supabase sync coming in the next pass.</div>
        </div>
      )}
      {activeTab === 'legal' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {[
            { title: 'Terms of Service', desc: 'The full Artizan Terms of Service — your rights, our responsibilities, subscription terms, and platform rules.', updated: 'Last updated: May 2026' },
            { title: 'Privacy Policy', desc: 'How we collect, use, and protect your data. Your data is never sold. You can export or delete it at any time.', updated: 'Last updated: May 2026' },
            { title: 'DMCA Policy', desc: 'Copyright infringement reporting and takedown procedures.', updated: 'Last updated: April 2026' },
            { title: 'IP Guide', desc: 'Understanding intellectual property rights on Artizan — who owns what when you collaborate.', updated: 'Last updated: April 2026' },
            { title: 'Craft Commons Foundation Terms', desc: 'Terms governing donations, grants, and the Starving Artist Fund.', updated: 'Last updated: April 2026' },
          ].map((doc, i) => (
            <div key={i} style={{ ...S.card, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 600, color: T.cream, marginBottom: 4 }}>{doc.title}</div>
                <div style={{ fontSize: 12, color: T.text2, lineHeight: 1.6, marginBottom: 4 }}>{doc.desc}</div>
                <div style={{ fontSize: 10, color: T.text3 }}>{doc.updated}</div>
              </div>
              <button
                onClick={() => dispatch({ type: 'ADD_TOAST', payload: { type: 'info', message: doc.title + ' — full document available at artizancraft.app/legal', icon: '📄' } })}
                style={{ ...S.btn, ...S.btnGhost, fontSize: 11, padding: '6px 12px', whiteSpace: 'nowrap', marginLeft: 16, flexShrink: 0 }}
              >
                View →
              </button>
            </div>
          ))}
          <div style={{ ...S.card, background: 'rgba(212,168,83,0.06)', border: `1px solid ${T.gold}30` }}>
            <div style={{ fontSize: 12, color: T.text2, lineHeight: 1.8 }}>
              <div style={{ fontWeight: 700, color: T.gold, marginBottom: 6 }}>Your Data Rights</div>
              <div>• Request a full export of your Artizan data at any time</div>
              <div>• Request account deletion with a 30-day grace period</div>
              <div>• Your data is never sold to third parties</div>
              <div>• Contact: legal@artizancraft.app</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── PROFILE / THE STUDIO ──
function ProfileViewOld({ state, dispatch }) {
  const { user } = state;
  // Profile theme
  const PT = (() => {
    const accent = user.themeColor || T.gold;
    const bg = user.themeBg || T.s2;
    // Luminance check for text color
    const hex = bg.replace('#','');
    const r = parseInt(hex.slice(0,2),16), g = parseInt(hex.slice(2,4),16), b = parseInt(hex.slice(4,6),16);
    const lum = (0.299*r + 0.587*g + 0.114*b) / 255;
    const text = lum > 0.5 ? '#0A0A0F' : '#F5ECD7';
    const text2 = lum > 0.5 ? '#333333' : '#B8A99A';
    const card = user.themeCard || null;
    return { accent, bg, text, text2, card };
  })();
  const verScore = computeVerificationScore(user.verificationSignals);
  const craftScore = computeCraftLineScore(user.craftLineDimensions);
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [editBio, setEditBio] = useState('');
  const [editLocation, setEditLocation] = useState('');
  const [editCrafts, setEditCrafts] = useState('');
  const [saving, setSaving] = useState(false);
  const [activeEditTab, setActiveEditTab] = useState('basic');
  const [editThemeColor, setEditThemeColor] = useState('');
  const [editThemeBg, setEditThemeBg] = useState('');
  const [editMusicUrl, setEditMusicUrl] = useState('');
  const [editMusicLabel, setEditMusicLabel] = useState('');
  const [editFeaturedArtisans, setEditFeaturedArtisans] = useState([]);
  const [editBioSections, setEditBioSections] = useState([]);
  const [editFeaturedWork, setEditFeaturedWork] = useState([]);
  const [allProfiles, setAllProfiles] = useState([]);
  const [artisanSearch, setArtisanSearch] = useState('');
  const [featuredCount, setFeaturedCount] = useState(4);
  const [editThemeCard, setEditThemeCard] = useState('');
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [cropFile, setCropFile] = useState(null);

  const handleAvatarUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setCropFile(file);
  };

  const handleCropSave = async (croppedFile) => {
    setUploadingAvatar(true);
    const url = await uploadAvatar(user.id, croppedFile);
    if (url) dispatch({ type: 'SET_USER', payload: { avatarUrl: url } });
    setUploadingAvatar(false);
    setCropFile(null);
  };

  const CRAFT_OPTIONS = ['Actor','Model','Singer','Dancer','Photographer','Videographer','Director','Producer','Stage Manager','Costume Designer','Stylist','Lighting Designer','Sound Designer','Writer','Composer','Musician','DP'];

  const startEdit = async () => {
    setEditName(user.name || '');
    setEditBio(user.bio || '');
    setEditLocation(user.location || '');
    setEditCrafts((user.crafts || []).join(', '));
    setEditThemeColor(user.themeColor || '#C9A84C');
    setEditThemeBg(user.themeBg || '#0A0A0F');
    setEditThemeCard(user.themeCard || '');
    setEditMusicUrl(user.musicUrl || '');
    setEditMusicLabel(user.musicLabel || '');
    setEditFeaturedArtisans(user.featuredArtisans || []);
    setEditBioSections(user.bioSections || []);
    setEditFeaturedWork(user.featuredWork || []);
    setActiveEditTab('basic');
    getAllProfiles().then(setAllProfiles);
    setEditing(true);
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      const { updateProfile } = await import('./db');
      await updateProfile(user.id, {
        display_name: editName,
        bio: editBio,
        location: editLocation,
        crafts: editCrafts.split(',').map(c => c.trim()).filter(Boolean),
        theme_color: editThemeColor,
        theme_bg: editThemeBg,
        theme_card: editThemeCard,
        music_url: editMusicUrl,
        music_label: editMusicLabel,
        featured_artisans: editFeaturedArtisans,
        bio_sections: editBioSections,
        featured_work: editFeaturedWork,
      });
      dispatch({ type: 'SET_USER', payload: {
        name: editName, bio: editBio, location: editLocation,
        crafts: editCrafts.split(',').map(c => c.trim()).filter(Boolean),
        themeColor: editThemeColor,
        themeBg: editThemeBg,
        themeCard: editThemeCard,
        musicUrl: editMusicUrl,
        musicLabel: editMusicLabel,
        featuredArtisans: editFeaturedArtisans,
        bioSections: editBioSections,
        featuredWork: editFeaturedWork,
      }});
      dispatch({ type: 'ADD_TOAST', payload: { type: 'success', message: 'Profile updated ✦', icon: '✦' } });
      setEditing(false);
    } catch(e) {
      dispatch({ type: 'ADD_TOAST', payload: { type: 'error', message: 'Save failed', icon: '✕' } });
    }
    setSaving(false);
  };

  return (
    <div className="profile-container" style={{ maxWidth: 760, margin: '0 auto' }}>
      {cropFile && (
        <AvatarCrop
          file={cropFile}
          onSave={handleCropSave}
          onCancel={() => setCropFile(null)}
        />
      )}
      <div style={{ ...S.card, background: `linear-gradient(135deg, ${PT.bg} 0%, ${PT.bg}CC 100%)`, marginBottom: 20, position: 'relative', overflow: 'hidden', borderColor: `${PT.accent}40` }}>
        <div style={{ position: 'absolute', top: -60, right: -60, width: 200, height: 200, borderRadius: '50%', background: `${T.gold}08` }} />
        <div className="profile-hero-inner" style={{ display: 'flex', gap: 20, alignItems: 'flex-start', position: 'relative', flexWrap: 'wrap' }}>
          <div style={{ position:'relative', flexShrink:0 }}>
            {user.avatarUrl
              ? <img src={user.avatarUrl} alt="avatar" style={{ width:72, height:72, borderRadius:'50%', objectFit:'cover', border:`2px solid ${PT.accent}` }} />
              : <Avatar emoji={user.avatar} size={72} border />
            }
            {editing && (
              <label style={{ position:'absolute', bottom:0, right:0, width:22, height:22, borderRadius:'50%', background:T.gold, display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', fontSize:12 }}>
                {uploadingAvatar ? '...' : '📷'}
                <input type="file" accept="image/*" onChange={handleAvatarUpload} style={{ display:'none' }} />
              </label>
            )}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
              <h1 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 26, fontWeight: 700, color: PT.text }}>{user.name}</h1>
              <Shield size={16} color={T.gold} fill={T.gold} />
            </div>
            <div style={{ fontSize: 13, color: PT.text2, marginBottom: 8 }}>{user.handle} · {user.location || 'Add your location'}</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
              {(user.crafts || []).map(c => <Badge key={c} color={PT.accent} bg={`${PT.accent}15`}>{c}</Badge>)}
            </div>
            <p style={{ fontSize: 14, color: PT.text2, lineHeight: 1.6 }}>{user.bio || 'Add a bio'}</p>
          </div>
          <button className="profile-edit-btn" style={{ ...S.btn, ...S.btnGhost }} onClick={startEdit}>
            <Edit size={14} /> Edit Studio
          </button>
        </div>
        <div className="profile-stats-row" style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12, marginTop: 20, paddingTop: 20, borderTop: `1px solid ${T.border}` }}>
          {[
            { label: 'Followers', value: user.followers.toLocaleString() },
            { label: 'Following', value: user.following.toLocaleString() },
            { label: 'Connections', value: user.connections.toLocaleString() },
            { label: 'Craft Line', value: craftScore },
            { label: 'Verification', value: verScore },
          ].map(stat => (
            <div key={stat.label} style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 22, fontWeight: 700, fontFamily: 'Cormorant Garamond, serif', color: PT.accent }}>{stat.value}</div>
              <div style={{ fontSize: 11, color: PT.text2, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{stat.label}</div>
            </div>
          ))}
        </div>
      </div>

      {editing && (
        <div style={{ ...S.card, border: `1px solid ${T.gold}40`, marginBottom: 20 }}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:18 }}>
            <h2 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 22, color: T.gold, margin:0 }}>Edit Studio</h2>
            <div style={{ display:'flex', gap:4, background:T.s2, borderRadius:8, padding:3 }}>
              {['basic','theme','music','artisans','sections','work'].map(tab => (
                <button key={tab} onClick={() => setActiveEditTab(tab)} style={{ padding:'4px 12px', borderRadius:6, fontSize:11, fontWeight:600, fontFamily:'inherit', cursor:'pointer', border:'none', background: activeEditTab === tab ? T.s4 : 'transparent', color: activeEditTab === tab ? T.gold : T.text2, textTransform:'capitalize' }}>{tab}</button>
              ))}
            </div>
          </div>
          {activeEditTab === 'basic' && <>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
            <div>
              <label style={{ fontSize: 11, color: T.text3, textTransform: 'uppercase', letterSpacing: '.06em', display: 'block', marginBottom: 5 }}>Display Name</label>
              <input value={editName} onChange={e => setEditName(e.target.value)} style={{ ...S.input }} />
            </div>
            <div>
              <label style={{ fontSize: 11, color: T.text3, textTransform: 'uppercase', letterSpacing: '.06em', display: 'block', marginBottom: 5 }}>Location</label>
              <input value={editLocation} onChange={e => setEditLocation(e.target.value)} placeholder="City, State" style={{ ...S.input }} />
            </div>
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 11, color: T.text3, textTransform: 'uppercase', letterSpacing: '.06em', display: 'block', marginBottom: 5 }}>Bio</label>
            <textarea value={editBio} onChange={e => setEditBio(e.target.value)} rows={3} style={{ ...S.input, resize: 'vertical', minHeight: 80 }} />
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 11, color: T.text3, textTransform: 'uppercase', letterSpacing: '.06em', display: 'block', marginBottom: 8 }}>Crafts</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {CRAFT_OPTIONS.map(craft => {
                const selected = editCrafts.split(',').map(c => c.trim()).includes(craft);
                return (
                  <button key={craft} onClick={() => {
                    const list = editCrafts.split(',').map(c => c.trim()).filter(Boolean);
                    if (selected) setEditCrafts(list.filter(c => c !== craft).join(', '));
                    else setEditCrafts([...list, craft].join(', '));
                  }} style={{ padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', background: selected ? `${T.gold}20` : T.s3, border: `1px solid ${selected ? T.gold : T.border2}`, color: selected ? T.gold : T.text2 }}>
                    {craft}
                  </button>
                );
              })}
            </div>
          </div>
          </>}

          {activeEditTab === 'theme' && (
            <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
              <div>
                <label style={{ fontSize:11, color:T.text3, textTransform:'uppercase', letterSpacing:'.06em', display:'block', marginBottom:8 }}>Accent Color</label>
                <div style={{ display:'flex', gap:12, alignItems:'center' }}>
                  <input type="color" value={editThemeColor} onChange={e => setEditThemeColor(e.target.value)} style={{ width:48, height:40, borderRadius:8, border:'none', cursor:'pointer', background:'none' }} />
                  <span style={{ fontSize:13, color:T.text2, fontFamily:'DM Mono, monospace' }}>{editThemeColor}</span>
                </div>
                <div style={{ display:'flex', gap:8, marginTop:10, flexWrap:'wrap' }}>
                  {['#C9A84C','#00BCD4','#9C27B0','#E91E63','#4CAF50','#FF5722','#2196F3','#FF9800'].map(col => (
                    <button key={col} onClick={() => setEditThemeColor(col)} style={{ width:28, height:28, borderRadius:'50%', background:col, border: editThemeColor === col ? '3px solid white' : '2px solid transparent', cursor:'pointer' }} />
                  ))}
                </div>
              </div>
              <div>
                <label style={{ fontSize:11, color:T.text3, textTransform:'uppercase', letterSpacing:'.06em', display:'block', marginBottom:8 }}>Background Color</label>
                <div style={{ display:'flex', gap:12, alignItems:'center' }}>
                  <input type="color" value={editThemeBg} onChange={e => setEditThemeBg(e.target.value)} style={{ width:48, height:40, borderRadius:8, border:'none', cursor:'pointer', background:'none' }} />
                  <span style={{ fontSize:13, color:T.text2, fontFamily:'DM Mono, monospace' }}>{editThemeBg}</span>
                </div>
                <div style={{ display:'flex', gap:8, marginTop:10, flexWrap:'wrap' }}>
                  {['#0A0A0F','#0D1117','#1A1A2E','#0F0F1A','#1C1C1E','#111827','#0F172A','#1A0A00'].map(col => (
                    <button key={col} onClick={() => setEditThemeBg(col)} style={{ width:28, height:28, borderRadius:'50%', background:col, border: editThemeBg === col ? '3px solid white' : `2px solid ${T.border2}`, cursor:'pointer' }} />
                  ))}
                </div>
              </div>
              <div style={{ marginBottom:16 }}>
                <label style={{ fontSize:11, color:T.text3, textTransform:'uppercase', letterSpacing:'.06em', display:'block', marginBottom:8 }}>Card/Pod Color</label>
                <div style={{ display:'flex', gap:12, alignItems:'center' }}>
                  <input type="color" value={editThemeCard || '#13131A'} onChange={e => setEditThemeCard(e.target.value)} style={{ width:48, height:40, borderRadius:8, border:'none', cursor:'pointer', background:'none' }} />
                  <span style={{ fontSize:13, color:T.text2, fontFamily:'DM Mono, monospace' }}>{editThemeCard || 'Default'}</span>
                  {editThemeCard && <button onClick={() => setEditThemeCard('')} style={{ background:'none', border:'none', color:T.text3, cursor:'pointer', fontSize:12, fontFamily:'inherit' }}>Reset</button>}
                </div>
                <div style={{ display:'flex', gap:8, marginTop:10, flexWrap:'wrap' }}>
                  {['#13131A','#1A1A2E','#1C1C2E','#0F1923','#1A0A00','#001A0F','#1A001A','#0A1A1A'].map(col => (
                    <button key={col} onClick={() => setEditThemeCard(col)} style={{ width:28, height:28, borderRadius:8, background:col, border: editThemeCard === col ? '3px solid white' : `2px solid ${T.border2}`, cursor:'pointer' }} />
                  ))}
                </div>
              </div>
              <div style={{ ...S.card, background: editThemeBg, border:`2px solid ${editThemeColor}`, padding:'1rem', borderRadius:12 }}>
                <p style={{ color: editThemeColor, fontFamily:'Cormorant Garamond, serif', fontSize:16, margin:0 }}>Preview — {editThemeColor} on {editThemeBg}</p>
              </div>
            </div>
          )}

          {activeEditTab === 'music' && (
            <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
              <p style={{ fontSize:13, color:T.text2, margin:0 }}>Add a track that plays on your profile. Supports YouTube, SoundCloud, and Spotify links.</p>
              <div>
                <label style={{ fontSize:11, color:T.text3, textTransform:'uppercase', letterSpacing:'.06em', display:'block', marginBottom:5 }}>Track URL</label>
                <input value={editMusicUrl} onChange={e => setEditMusicUrl(e.target.value)} placeholder="https://open.spotify.com/track/..." style={{ ...S.input }} />
              </div>
              <div>
                <label style={{ fontSize:11, color:T.text3, textTransform:'uppercase', letterSpacing:'.06em', display:'block', marginBottom:5 }}>Label (shown on profile)</label>
                <input value={editMusicLabel} onChange={e => setEditMusicLabel(e.target.value)} placeholder="e.g. Now Playing: Before the Throne" style={{ ...S.input }} />
              </div>
              {editMusicUrl && (
                <div style={{ background:T.s2, borderRadius:10, padding:'0.75rem', fontSize:13, color:T.text2 }}>
                  🎵 {editMusicLabel || editMusicUrl}
                </div>
              )}
            </div>
          )}

          {activeEditTab === 'artisans' && (
            <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                <p style={{ fontSize:13, color:T.text2, margin:0 }}>Feature up to {featuredCount} artisans on your profile.</p>
                <select value={featuredCount} onChange={e => setFeaturedCount(parseInt(e.target.value))} style={{ ...S.input, width:'auto', padding:'4px 8px' }}>
                  {[2,4,6,8,10].map(n => <option key={n} value={n}>{n} artisans</option>)}
                </select>
              </div>
              <input placeholder="Search by name or handle..." value={artisanSearch} onChange={e => setArtisanSearch(e.target.value)} style={{ ...S.input }} />
              {artisanSearch.trim().length > 0 && (
                <div style={{ maxHeight:160, overflowY:'auto', background:T.s2, borderRadius:8, padding:'0.5rem' }}>
                  {allProfiles.filter(p => p.id !== user.id && !editFeaturedArtisans.includes(p.id) && ((p.display_name||'').toLowerCase().includes(artisanSearch.toLowerCase()) || (p.handle||'').toLowerCase().includes(artisanSearch.toLowerCase()))).slice(0,6).map(p => (
                    <div key={p.id} onClick={() => { if (editFeaturedArtisans.length < featuredCount) { setEditFeaturedArtisans(prev => [...prev, p.id]); setArtisanSearch(''); } }} style={{ display:'flex', alignItems:'center', gap:10, padding:'0.4rem 0.5rem', cursor:'pointer', borderRadius:6 }}>
                      {p.avatar_url ? <img src={p.avatar_url} alt="av" style={{ width:28, height:28, borderRadius:'50%', objectFit:'cover' }} /> : <Avatar emoji="👤" size={28} />}
                      <div style={{ fontSize:13, color:T.cream }}>{p.display_name} <span style={{ color:T.text3 }}>{p.handle}</span></div>
                    </div>
                  ))}
                </div>
              )}
              <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
                {editFeaturedArtisans.slice(0, featuredCount).map(id => {
                  const p = allProfiles.find(x => x.id === id);
                  return p ? (
                    <div key={id} style={{ display:'flex', alignItems:'center', gap:6, background:T.s2, borderRadius:20, padding:'4px 10px 4px 6px' }}>
                      {p.avatar_url ? <img src={p.avatar_url} alt="av" style={{ width:22, height:22, borderRadius:'50%', objectFit:'cover' }} /> : <Avatar emoji="👤" size={22} />}
                      <span style={{ fontSize:12, color:T.cream }}>{p.display_name}</span>
                      <button onClick={() => setEditFeaturedArtisans(prev => prev.filter(x => x !== id))} style={{ background:'none', border:'none', color:T.text3, cursor:'pointer', fontSize:14, lineHeight:1 }}>✕</button>
                    </div>
                  ) : null;
                })}
              </div>
            </div>
          )}

          {activeEditTab === 'sections' && (
            <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
              <p style={{ fontSize:13, color:T.text2, margin:0 }}>Add custom sections to your profile — credits, awards, statement, anything.</p>
              {editBioSections.map((sec, i) => (
                <div key={i} style={{ background:T.s2, borderRadius:10, padding:'0.75rem', display:'flex', flexDirection:'column', gap:8 }}>
                  <div style={{ display:'flex', gap:8 }}>
                    <input placeholder="Section title (e.g. Credits)" value={sec.title} onChange={e => setEditBioSections(prev => prev.map((s,j) => j===i ? {...s, title:e.target.value} : s))} style={{ ...S.input, flex:1 }} />
                    <button onClick={() => setEditBioSections(prev => prev.filter((_,j) => j!==i))} style={{ background:'none', border:`1px solid ${T.border2}`, borderRadius:8, padding:'0 10px', color:T.text3, cursor:'pointer', fontFamily:'inherit' }}>✕</button>
                  </div>
                  <textarea placeholder="Content..." value={sec.content} onChange={e => setEditBioSections(prev => prev.map((s,j) => j===i ? {...s, content:e.target.value} : s))} rows={3} style={{ ...S.input, resize:'vertical' }} />
                </div>
              ))}
              <button onClick={() => setEditBioSections(prev => [...prev, { title:'', content:'' }])} style={{ ...S.btn, ...S.btnGhost, alignSelf:'flex-start' }}>+ Add Section</button>
            </div>
          )}

          {activeEditTab === 'work' && (
            <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
              <p style={{ fontSize:13, color:T.text2, margin:0 }}>Spotlight your best work — add links, titles, and descriptions.</p>
              {editFeaturedWork.map((w, i) => (
                <div key={i} style={{ background:T.s2, borderRadius:10, padding:'0.75rem', display:'flex', flexDirection:'column', gap:8 }}>
                  <div style={{ display:'flex', gap:8 }}>
                    <input placeholder="Title (e.g. Femme — Lead Role)" value={w.title} onChange={e => setEditFeaturedWork(prev => prev.map((x,j) => j===i ? {...x, title:e.target.value} : x))} style={{ ...S.input, flex:1 }} />
                    <button onClick={() => setEditFeaturedWork(prev => prev.filter((_,j) => j!==i))} style={{ background:'none', border:`1px solid ${T.border2}`, borderRadius:8, padding:'0 10px', color:T.text3, cursor:'pointer', fontFamily:'inherit' }}>✕</button>
                  </div>
                  <input placeholder="Link (optional)" value={w.url||''} onChange={e => setEditFeaturedWork(prev => prev.map((x,j) => j===i ? {...x, url:e.target.value} : x))} style={{ ...S.input }} />
                  <textarea placeholder="Description..." value={w.description||''} onChange={e => setEditFeaturedWork(prev => prev.map((x,j) => j===i ? {...x, description:e.target.value} : x))} rows={2} style={{ ...S.input, resize:'vertical' }} />
                </div>
              ))}
              <button onClick={() => setEditFeaturedWork(prev => [...prev, { title:'', url:'', description:'' }])} style={{ ...S.btn, ...S.btnGhost, alignSelf:'flex-start' }}>+ Add Work</button>
            </div>
          )}

          <div style={{ display: 'flex', gap: 10, marginTop:16 }}>
            <button onClick={() => setEditing(false)} style={{ ...S.btn, ...S.btnGhost }}>Cancel</button>
            <button onClick={saveEdit} disabled={saving} style={{ ...S.btn, ...S.btnGold, opacity: saving ? 0.7 : 1 }}>
              {saving ? 'Saving...' : 'Save Profile'}
            </button>
          </div>
        </div>
      )}

      {/* Music Player */}
      {user.musicUrl && (
        <div style={{ ...S.card, marginBottom:16, display:'flex', alignItems:'center', gap:12, ...(PT.card ? { background: PT.card } : {}) }}>
          <div style={{ width:36, height:36, borderRadius:'50%', background:`${T.gold}20`, border:`1px solid ${T.gold}`, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
            <Music size={16} color={T.gold} />
          </div>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ fontSize:11, color:T.text3, textTransform:'uppercase', letterSpacing:'.06em', marginBottom:2 }}>Now Playing</div>
            <a href={sanitizeUrl(user.musicUrl)} target="_blank" rel="noopener noreferrer" style={{ fontSize:14, color:T.cream, textDecoration:'none', fontWeight:600, display:'block', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{user.musicLabel || user.musicUrl}</a>
          </div>
          <a href={sanitizeUrl(user.musicUrl)} target="_blank" rel="noopener noreferrer" style={{ ...S.btn, ...S.btnGhost, padding:'6px 12px', fontSize:12, textDecoration:'none' }}>▶ Open</a>
        </div>
      )}

      {/* Featured Artisans */}
      {(user.featuredArtisans || []).length > 0 && (
        <div style={{ ...S.card, marginBottom:16, background: PT.card || undefined }}>
          <div style={{ fontSize:12, color:T.text3, textTransform:'uppercase', letterSpacing:'.08em', marginBottom:14 }}>Top Artisans</div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(120px, 1fr))', gap:10 }}>
            {(user.featuredArtisans || []).map(id => {
              const p = (user._featuredProfiles || []).find(x => x.id === id);
              return (
                <div key={id} style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:6, padding:'0.75rem 0.5rem', background:T.s2, borderRadius:10 }}>
                  {p?.avatar_url ? <img src={p.avatar_url} alt="av" style={{ width:44, height:44, borderRadius:'50%', objectFit:'cover', border:`2px solid ${T.gold}40` }} /> : <Avatar emoji="👤" size={44} />}
                  <div style={{ fontSize:12, fontWeight:600, color:T.cream, textAlign:'center' }}>{p?.display_name || '...'}</div>
                  <div style={{ fontSize:11, color:T.text3 }}>{(p?.crafts||[]).slice(0,1).join('')}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Bio Sections */}
      {(user.bioSections || []).filter(s => s.title || s.content).map((sec, i) => (
        <div key={i} style={{ ...S.card, marginBottom:16, background: PT.card || S.card.background }}>
          {sec.title && <div style={{ fontSize:12, color:T.text3, textTransform:'uppercase', letterSpacing:'.08em', marginBottom:10 }}>{sec.title}</div>}
          <p style={{ fontSize:14, color:T.text2, lineHeight:1.7, margin:0 }}>{sec.content}</p>
        </div>
      ))}

      {/* Featured Work */}
      {(user.featuredWork || []).filter(w => w.title).length > 0 && (
        <div style={{ ...S.card, marginBottom:16, ...(PT.card ? { background: PT.card } : {}) }}>
          <div style={{ fontSize:12, color:PT.text2, textTransform:'uppercase', letterSpacing:'.08em', marginBottom:14 }}>Featured Work</div>
          <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
            {(user.featuredWork || []).filter(w => w.title).map((w, i) => (
              <div key={i} style={{ borderLeft:`3px solid ${T.gold}`, paddingLeft:12 }}>
                <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4 }}>
                  <span style={{ fontWeight:600, fontSize:14, color:T.cream }}>{w.title}</span>
                  {w.url && <a href={w.url} target="_blank" rel="noopener noreferrer" style={{ fontSize:11, color:T.gold, textDecoration:'none' }}>↗ View</a>}
                </div>
                {w.description && <p style={{ fontSize:13, color:T.text2, margin:0, lineHeight:1.6 }}>{w.description}</p>}
              </div>
            ))}
          </div>
        </div>
      )}

            <div className="profile-modules-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <div style={{ ...S.card, ...(PT.card ? { background: PT.card } : {}) }}><div style={{ fontSize: 12, color: T.text3, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 12 }}>The Reel</div><div style={{ background: T.s3, borderRadius: 10, padding: '1.5rem', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}><div style={{ width: 48, height: 48, borderRadius: '50%', background: G.gold, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Music size={20} color={T.bg} /></div><p style={{ fontSize: 13, color: T.text2, textAlign: 'center' }}>Audio portfolio reel</p><button style={{ ...S.btn, ...S.btnGhost, fontSize: 12 }}><Play size={12} /> Play</button></div></div>
        <div style={{ ...S.card, ...(PT.card ? { background: PT.card } : {}) }}><div style={{ fontSize: 12, color: T.text3, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 12 }}>The Showreel</div><div style={{ background: T.s3, borderRadius: 10, padding: '1.5rem', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}><div style={{ width: 48, height: 48, borderRadius: '50%', background: G.purple, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Video size={20} color="white" /></div><p style={{ fontSize: 13, color: T.text2, textAlign: 'center' }}>Video showreel</p><button style={{ ...S.btn, ...S.btnGhost, fontSize: 12 }}><Play size={12} /> Play</button></div></div>
        <div style={S.card}><div style={{ fontSize: 12, color: T.text3, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 12 }}>Portfolio</div><div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>{['📸','🎭','📷','🎬'].map((emoji,i) => (<div key={i} style={{ background: T.s3, borderRadius: 8, aspectRatio: '1', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, cursor: 'pointer' }}>{emoji}</div>))}</div><button style={{ ...S.btn, ...S.btnGhost, width: '100%', justifyContent: 'center', marginTop: 12 }}><Upload size={14} /> Upload Work</button></div>
        <div style={S.card}><div style={{ fontSize: 12, color: T.text3, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 12 }}>About</div><p style={{ fontSize: 14, color: T.text2, lineHeight: 1.7 }}>{user.bio || 'Add a bio to tell people about your creative practice.'}</p></div>
      </div>
    </div>
  );
}

// ── NOTIFICATIONS PANEL ──
function NotificationsPanel({ state, dispatch, onClose, realNotifications, loadRealNotifications, userId }) {
  const notifications = realNotifications && realNotifications.length > 0 ? realNotifications : state.notifications;
  const handleMarkAllRead = async () => {
    if (userId && realNotifications.length > 0) {
      await markNotificationsRead(userId);
      if (loadRealNotifications) await loadRealNotifications();
    }
    dispatch({ type: 'MARK_NOTIFICATIONS_READ' });
  };

  useEffect(() => {
    dispatch({ type: 'MARK_NOTIFS_READ' });
  }, [dispatch]);

  const typeIcon = { connection: '🤝', cast: '✦', verification: '🛡️', message: '💬' };

  return (
    <div style={{
      position: 'fixed', top: 58, right: 16, width: 340, maxHeight: '70vh',
      overflow: 'auto', background: T.s2, border: `1px solid ${T.border2}`,
      borderRadius: 12, boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
      zIndex: 200,
    }}>
      <div style={{ padding: '1rem 1.25rem', borderBottom: `1px solid ${T.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontWeight: 600, fontSize: 14 }}>Notifications</span>
<button onClick={handleMarkAllRead} style={{ ...S.btn, ...S.btnGhost, padding: '4px 8px', fontSize:11 }}>Mark all read</button>
        <button onClick={onClose} style={{ ...S.btn, ...S.btnGhost, padding: '4px 8px' }}><X size={14} /></button>
      </div>
      {notifications.length === 0 && <div style={{ padding:'2rem', textAlign:'center', color:T.text3, fontSize:13 }}>No notifications yet.</div>}
      {notifications.map(notif => (
        <div key={notif.id} style={{ padding: '0.9rem 1.25rem', borderBottom: `1px solid ${T.border}`, display: 'flex', gap: 12, alignItems: 'flex-start', background: notif.read === false ? `${T.gold}08` : 'transparent' }}>
          <span style={{ fontSize: 18 }}>{typeIcon[notif.type] || '●'}</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, color: T.text, marginBottom: 2, fontWeight: notif.read === false ? 600 : 400 }}>{notif.title || notif.text}</div>
            {(notif.message) && <div style={{ fontSize: 12, color: T.text2, marginBottom: 2 }}>{notif.message}</div>}
            <div style={{ fontSize: 11, color: T.text3 }}>{notif.created_at ? new Date(notif.created_at).toLocaleDateString() : notif.time}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────
// NAVIGATION
// ─────────────────────────────────────────────
const NAV_ITEMS = [
  { id: 'feed',        label: 'The Reel',    icon: Home },
  { id: 'search',      label: 'Search',      icon: Search },
  { id: 'talent',      label: 'Talent',      icon: Search },
  { id: 'casting',     label: 'Casting',     icon: Layers },
  { id: 'jobs',        label: 'Job Board',   icon: Briefcase },
  { id: 'connections', label: 'Connections', icon: Users },
  { id: 'messages',    label: 'Messages',    icon: MessageCircle },
  { id: 'aisle',        label: 'Aisle',        icon: Compass },
  { id: 'vizion',       label: 'Vizion',       icon: Video },
  { id: 'curtainz',     label: 'Curtainz',     icon: Clapperboard },
  { id: 'auditions',    label: 'Auditions',    icon: ClipboardList },
  { id: 'selftape',     label: 'Self-Tape',    icon: Video },
  { id: 'contracts',    label: 'Contracts',    icon: FileText },
  { id: 'roster',       label: 'Roster',       icon: Users },
  { id: 'analytics',   label: 'Analytics',   icon: BarChart2 },
  { id: 'profile',     label: 'The Studio',  icon: Layers },
  { id: 'settings',    label: 'Settings',    icon: Settings },
];

// ─────────────────────────────────────────────
// ROOT COMPONENT
// ─────────────────────────────────────────────
export default function ArtistryConnect(props) {
  const [state, dispatch] = useReducer(reducer, { ...initialState, onCreatePost: props.onCreatePost });

  // Read tour mock data from sessionStorage if present
  const tourMock = (() => {
    try { return JSON.parse(sessionStorage.getItem('artizan_tour_mock') || 'null'); }
    catch { return null; }
  })();

  // Feature tour state
  const [activeTour, setActiveTour] = useState(null);
  const [onboardingTourMode, setOnboardingTourMode] = useState(false);
  const [showCreateSheet, setShowCreateSheet] = useState(false);
  const [showMoreSheet, setShowMoreSheet] = useState(false);
  const [explored, setExplored] = useState({});
  const [reminderShown, setReminderShown] = useState(false);

  // Load feature progress
  useEffect(() => {
    if (!props.user?.id) return;
    getFeatureProgress(props.user.id).then(data => {
      setExplored(data.features_explored || {});
      // Weekly reminder check
      const last = data.feature_reminder_last_shown;
      const weekAgo = new Date(Date.now() - 7*24*60*60*1000);
      const unexplored = FEATURES.filter(f => !data.features_explored?.[f.id]);
      if (unexplored.length > 0 && (!last || new Date(last) < weekAgo) && !reminderShown) {
        setReminderShown(true);
        markReminderShown(props.user.id);
        setTimeout(() => setActiveTour(unexplored[0].id), 3000);
      }
    });
  }, [props.user?.id]);

  // Mark feature as explored when view changes
  useEffect(() => {
    const view = state.view;
    if (!props.user?.id || !view) return;
    const featureMap = { feed:'reel', search:'search', talent:'talent', casting:'casting', jobs:'jobs', connections:'connections', messages:'messages', aisle:'aisle', vizion:'vizion', curtainz:'curtainz', profile:'studio', settings:'settings' };
    const featureId = featureMap[view];
    if (featureId && !explored[featureId]) {
      markFeatureExplored(props.user.id, featureId);
      setExplored(prev => ({ ...prev, [featureId]: new Date().toISOString() }));
    }
  }, [state.view]);

  const startTour = (featureId) => {
    const feature = FEATURES.find(f => f.id === featureId);
    if (feature?.nav) dispatch({ type: 'SET_VIEW', payload: feature.nav });
    setActiveTour(featureId);
  };

  // Navigate to a specific view when onboarding directs us
  useEffect(() => {
    if (props.initialView) {
      dispatch({ type: 'SET_VIEW', payload: props.initialView });
    }
  }, [props.initialView]);

  // Sync real profile into state when it arrives
  useEffect(() => {
    if (props.profile) {
      dispatch({ type: 'SET_USER', payload: {
        ...initialState.user,
        id: props.profile.id,
        name: props.profile.display_name || 'Your Name',
        handle: props.profile.handle || '@handle',
        bio: props.profile.bio || '',
        location: props.profile.location || '',
        crafts: props.profile.crafts || [],
        available: props.profile.available !== false,
        avatarUrl: props.profile.avatar_url || null,
        themeColor: props.profile.theme_color || null,
        themeBg: props.profile.theme_bg || null,
        themeCard: props.profile.theme_card || null,
        musicUrl: props.profile.music_url || null,
        musicLabel: props.profile.music_label || null,
        featuredArtisans: props.profile.featured_artisans || [],
        bioSections: props.profile.bio_sections || [],
        featuredWork: props.profile.featured_work || [],
        subscription: props.profile.subscription_tier || props.profile.subscription || 'free',
      }});
    }
  }, [props.profile]);
  const [dataLoading, setDataLoading] = useState(true);
  const [realPosts, setRealPosts] = useState([]);
  const [realJobs, setRealJobs] = useState([]);
  const [realConnections, setRealConnections] = useState([]);
  const [realVerification, setRealVerification] = useState(null);
  const [realStats, setRealStats] = useState(null);
  const [realNotifications, setRealNotifications] = useState([]);
  const [realProfiles, setRealProfiles] = useState([]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { Promise.all([loadRealPosts(), loadRealJobs(), loadRealConnections(), loadRealVerification(), loadRealProfiles(), loadRealNotifications()]).finally(() => setDataLoading(false)); }, []);
  const loadRealPosts = async () => { const data = await getPosts(); setRealPosts(data); };
  const loadRealJobs = async () => { const data = await getJobs(); setRealJobs(data); };
  const loadRealConnections = async () => { if (props.user?.id) { const data = await getConnections(props.user.id); setRealConnections(data); } };
  const loadRealVerification = async () => { if (props.user?.id) { const data = await getVerificationSignals(props.user.id); setRealVerification(data); } };
  const loadRealStats = async () => { if (props.user?.id) { const data = await getProfileStats(props.user.id); setRealStats(data); } };
  const loadRealNotifications = async () => { if (props.user?.id) { const data = await getNotifications(props.user.id); setRealNotifications(data); } };
  const loadRealProfiles = async () => { const data = await getAllProfiles(); setRealProfiles(data); };
  const [showNotifs, setShowNotifs] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const unreadCount = realNotifications.length > 0 ? realNotifications.filter(n => !n.read).length : state.notifications.filter(n => !n.read).length;

  // Inject Google Fonts
  useEffect(() => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,600;0,700;1,400&family=DM+Sans:wght@300;400;500;600&family=DM+Mono&display=swap';
    document.head.appendChild(link);

    // Inject global styles
    const style = document.createElement('style');
    style.textContent = `
      *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
      body { background: ${T.bg}; color: ${T.text}; font-family: 'DM Sans', sans-serif; }
      ::-webkit-scrollbar { width: 4px; }
      ::-webkit-scrollbar-track { background: ${T.bg}; }
      ::-webkit-scrollbar-thumb { background: ${T.amber}; border-radius: 2px; }
      @keyframes slideIn { from { opacity: 0; transform: translateX(20px); } to { opacity: 1; transform: translateX(0); } }
      a { color: ${T.gold}; text-decoration: none; }
    `;
    document.head.appendChild(style);

    // Supabase Realtime — live feed updates
    const channel = supabase
      .channel("global-posts")
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "posts" }, () => { loadRealPosts(); })
      .subscribe();
    return () => supabase.removeChannel(channel);
  }, []);

  const renderView = () => {
    const props = { state, dispatch };
    switch (state.view) {
      case 'search':     return <SearchView     {...props} userId={state.user?.id} />;
      case 'feed':        return <FeedView tourMode={onboardingTourMode} tourMode={onboardingTourMode}        {...props} realPosts={tourMock ? [...(tourMock.feed||[]), ...realPosts] : realPosts} loadRealPosts={loadRealPosts} />;
      case 'talent':      return <TalentView      {...props} realProfiles={tourMock ? [...(tourMock.talent||[]), ...realProfiles] : realProfiles} userId={state.user?.id} />;
      case 'jobs':        return <JobsView        {...props} realJobs={tourMock ? [...(tourMock.jobs||[]), ...realJobs] : realJobs} loadRealJobs={loadRealJobs} user={props.user} />;
      case 'casting':     return <CastingView     {...props} userId={state.user?.id} verificationScore={state.user?.verificationScore} />;
      case 'connections': return <ConnectionsView {...props} realConnections={realConnections} loadRealConnections={loadRealConnections} userId={state.user?.id} />;
      case 'messages':    return <MessagesView    {...props} userId={state.user?.id} />;
      case 'aisle':        return <AisleView />;
      case 'vizion':       return <VizionView userId={state.user?.id} />;
          case 'curtainz':     return <CurtainzView userId={state.user?.id} />;
          case 'auditions':    return <AuditionTracker userId={state.user?.id} />;
          case 'selftape':     return <SelfTapeStudio userId={state.user?.id} userName={state.user?.name} />;
          case 'contracts':    return <ContractVault userId={state.user?.id} />;
          case 'roster':       return <RosterManager userId={state.user?.id} repType={state.user?.account_type} />;
          case 'foundation':   return <CraftCommons />;
      case 'dashboard':   return <AnalyticsDashboard userId={state.user?.id} />;
          case 'analytics':   return <AnalyticsView   {...props} realVerification={realVerification} realStats={realStats} userId={state.user?.id} loadRealVerification={loadRealVerification} />;
      case 'profile':     return <ProfileViewOld state={state} dispatch={dispatch} />;
      case 'settings':    return <SettingsView    {...props} />;
      default:            return <FeedView        {...props} realPosts={realPosts} loadRealPosts={loadRealPosts} />;
    }
  };

  const viewTitles = {
    feed: 'The Reel', talent: 'Talent Search', jobs: 'Job Board',
    connections: 'Connections — The Company', messages: 'Messages',
    analytics: 'Analytics', profile: 'The Studio', settings: 'Settings',
  };

  return (
    <div className="app-layout" style={{ display: 'flex', minHeight: '100vh', background: T.bg }}>
      {/* Sidebar */}
      <nav className="sidebar-nav" style={{
        width: state.sidebarOpen ? 240 : 68, flexShrink: 0,
        background: T.s1, borderRight: `1px solid ${T.border}`,
        display: isMobile ? 'none' : 'flex', flexDirection: 'column',
        transition: 'width 0.25s ease', overflow: 'hidden',
        position: 'sticky', top: 0, height: '100vh',
      }}>
        {/* Logo */}
        <div style={{ padding: '1.25rem', borderBottom: `1px solid ${T.border}`, display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 36, height: 36, borderRadius: 10, background: G.gold, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <span style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 700, fontSize: 18, color: T.bg }}>A</span>
          </div>
          {state.sidebarOpen && (
            <span style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 20, fontWeight: 700, color: T.gold, whiteSpace: 'nowrap' }}>
              Artizan
            </span>
          )}
        </div>

        {/* Nav items */}
        <div style={{ flex: 1, padding: '0.75rem 0.5rem', overflowY: 'auto' }}>
          {NAV_ITEMS.map(item => {
            const active = state.view === item.id;
            return (
              <button
                key={item.id}
                onClick={() => dispatch({ type: 'SET_VIEW', payload: item.id })}
                style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  width: '100%', padding: '0.65rem 0.75rem', borderRadius: 8, marginBottom: 2,
                  background: active ? `${T.gold}15` : 'transparent',
                  color: active ? T.gold : T.text2,
                  border: 'none', fontFamily: 'inherit', fontSize: 14, cursor: 'pointer',
                  transition: 'all 0.15s ease', fontWeight: active ? 600 : 400,
                  whiteSpace: 'nowrap',
                }}
              >
                <item.icon size={18} strokeWidth={active ? 2.5 : 1.5} style={{ flexShrink: 0 }} />
                {state.sidebarOpen && item.label}
              </button>
            );
          })}
        </div>

        {/* Foundation subtle link */}
        {state.sidebarOpen && (
          <div onClick={()=>dispatch({type:'SET_VIEW',payload:'foundation'})} style={{margin:'0 8px 4px',padding:'6px 10px',borderRadius:8,cursor:'pointer',display:'flex',alignItems:'center',gap:8,opacity:0.6,transition:'opacity 0.2s'}}
            onMouseEnter={e=>e.currentTarget.style.opacity='1'}
            onMouseLeave={e=>e.currentTarget.style.opacity='0.6'}>
            <span style={{fontSize:12,color:'#d4a853'}}>⧫</span>
            <span style={{fontSize:11,color:'#9890b0'}}>Craft Commons Foundation</span>
          </div>
        )}
        {/* Progress card */}
        {state.sidebarOpen && (
          <ProgressCard
            explored={explored}
            onStartTour={startTour}
            onTourStart={()=>setOnboardingTourMode(true)}
            onTourEnd={()=>setOnboardingTourMode(false)}
            onNavigate={(nav)=>dispatch({type:'SET_VIEW',payload:nav})}
          />
        )}
        {/* User footer */}
        {state.sidebarOpen && (
          <div style={{ padding: '0.75rem', borderTop: `1px solid ${T.border}` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '0.5rem', borderRadius: 8, background: T.s2 }}>
              {state.user.avatarUrl ? <img src={state.user.avatarUrl} alt="avatar" style={{width:32,height:32,borderRadius:'50%',objectFit:'cover',border:`2px solid ${T.gold}`}} /> : <Avatar emoji={state.user.avatar} size={32} border />}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' }}>{state.user.name}</div>
                <div style={{ fontSize: 10, color: T.text3 }}>{state.user.subscription.toUpperCase()}</div>
              </div>
              <button
                onClick={() => props.onLogout && props.onLogout()}
                title="Log out"
                style={{ background: 'none', border: `1px solid ${T.border2}`, borderRadius: 6, padding: '4px 8px', fontSize: 11, color: T.text3, cursor: 'pointer', fontFamily: 'inherit', flexShrink: 0 }}
              >
                ⎋
              </button>
            </div>
          </div>
        )}
      </nav>

      {/* ── BOTTOM NAV (mobile only) ── */}
      <MobileTabBar
        currentView={state.view}
        onNavigate={(v) => dispatch({ type: 'SET_VIEW', payload: v })}
        onCreateOpen={() => setShowCreateSheet(true)}
        onMoreOpen={() => setShowMoreSheet(true)}
      />
      <CreateSheet
        open={showCreateSheet}
        onClose={() => setShowCreateSheet(false)}
        onNavigate={(v) => dispatch({ type: 'SET_VIEW', payload: v })}
      />
      <MoreSheet
        open={showMoreSheet}
        onClose={() => setShowMoreSheet(false)}
        onNavigate={(v) => dispatch({ type: 'SET_VIEW', payload: v })}
        currentView={state.view}
      />

      {/* Main area */}
      <div className="main-content" style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        {/* Top bar */}
        <header style={{
          height: 58, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '0 1.5rem', borderBottom: `1px solid ${T.border}`,
          background: `${T.s1}cc`, backdropFilter: 'blur(10px)',
          position: 'sticky', top: 0, zIndex: 100,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button
              onClick={() => isMobile ? setShowMobileMenu(true) : dispatch({ type: 'TOGGLE_SIDEBAR' })}
              style={{ ...S.btn, ...S.btnGhost, padding: '6px 8px' }}
            >
              <Menu size={18} />
            </button>
            <h1 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 20, fontWeight: 700, color: T.gold }}>
              {viewTitles[state.view]}
            </h1>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button style={{ ...S.btn, ...S.btnGhost, padding: '6px 8px', position: 'relative' }}
              onClick={() => setShowNotifs(!showNotifs)}>
              <Bell size={18} />
              {unreadCount > 0 && (
                <span style={{
                  position: 'absolute', top: 2, right: 2,
                  width: 16, height: 16, borderRadius: '50%',
                  background: T.error, fontSize: 10, fontWeight: 700,
                  color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  {unreadCount}
                </span>
              )}
            </button>
            {state.user.avatarUrl ? <img src={state.user.avatarUrl} alt="avatar" style={{width:34,height:34,borderRadius:'50%',objectFit:'cover',border:`2px solid ${T.gold}`}} /> : <Avatar emoji={state.user.avatar} size={34} border />}
          </div>
        </header>

        {/* Content */}
        <main className="content-scroll" style={{ flex: 1, padding: '1.5rem', overflowY: 'auto' }}>
          {dataLoading ? (
            <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'60vh', flexDirection:'column', gap:16 }}>
              <div style={{ width:40, height:40, borderRadius:'50%', border:`3px solid ${T.gold}`, borderTopColor:'transparent', animation:'spin 0.8s linear infinite' }} />
              <span style={{ color:T.text2, fontSize:14 }}>Loading...</span>
              <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </div>
          ) : renderView()}
        </main>
      </div>

      {/* Notifications panel */}
      {/* Mobile Nav Drawer */}
      {isMobile && showMobileMenu && (
        <div style={{ position:'fixed', inset:0, zIndex:500 }} onClick={() => setShowMobileMenu(false)}>
          <div style={{ position:'absolute', left:0, top:0, bottom:0, width:260, background:T.s1, borderRight:`1px solid ${T.border2}`, display:'flex', flexDirection:'column', padding:'1rem 0' }} onClick={e => e.stopPropagation()}>
            <div style={{ padding:'0 1rem 1rem', borderBottom:`1px solid ${T.border}`, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <span style={{ fontFamily:'Cormorant Garamond,serif', fontSize:20, fontWeight:700, color:T.gold }}>Artizan</span>
              <button onClick={() => setShowMobileMenu(false)} style={{ background:'none', border:'none', color:T.text2, cursor:'pointer', fontSize:20 }}>✕</button>
            </div>
            {NAV_ITEMS.map(item => (
              <button key={item.id} onClick={() => { dispatch({ type:'SET_VIEW', payload:item.id }); setShowMobileMenu(false); }} style={{ display:'flex', alignItems:'center', gap:12, padding:'0.85rem 1.25rem', background: state.view===item.id ? `${T.gold}15` : 'none', border:'none', cursor:'pointer', color: state.view===item.id ? T.gold : T.text2, fontFamily:'inherit', fontSize:14, fontWeight: state.view===item.id ? 600 : 400, width:'100%', textAlign:'left' }}>
                <item.icon size={18} />
                {item.label}
              </button>
            ))}
          </div>
        </div>
      )}
      {showNotifs && (
        <NotificationsPanel state={state} dispatch={dispatch} onClose={() => setShowNotifs(false)} realNotifications={realNotifications} loadRealNotifications={loadRealNotifications} userId={state.user?.id} />
      )}

      {/* Feature Tour Overlay */}
      {activeTour && (
        <FeatureTour
          featureId={activeTour}
          onComplete={() => {
            markFeatureExplored(props.user?.id, activeTour);
            setExplored(prev => ({ ...prev, [activeTour]: new Date().toISOString() }));
            setActiveTour(null);
          }}
          onSkip={() => setActiveTour(null)}
        />
      )}
      {/* Sparky */}
      <Sparky onStartTour={() => { const unexplored = FEATURES.find(f => !explored[f.id]); if (unexplored) startTour(unexplored.id); }} />
      {/* Feedback button — bottom right */}
      <a
        href="mailto:jerone@studiojeada.com?subject=Artizan Beta Feedback&body=Screen: %0D%0AIssue: %0D%0ASteps to reproduce: "
        style={{
          position: 'fixed', bottom: 80, right: 16, zIndex: 998,
          background: 'rgba(13,12,24,0.92)', border: '1px solid rgba(212,168,83,0.3)',
          color: '#d4a853', borderRadius: 20, padding: '6px 14px',
          fontSize: 11, fontFamily: 'inherit', textDecoration: 'none',
          display: 'flex', alignItems: 'center', gap: 6,
          backdropFilter: 'blur(8px)', letterSpacing: '0.04em',
        }}
      >
        ✦ Send feedback
      </a>

      {/* Toast stack */}
      <div style={{ position: 'fixed', bottom: 20, right: 20, zIndex: 1000, width: 320 }}>
        {state.toasts.map(toast => (
          <Toast
            key={toast.id}
            toast={toast}
            onRemove={id => dispatch({ type: 'REMOVE_TOAST', payload: id })}
          />
        ))}
      </div>
    </div>
  );
}

/**
 * ─────────────────────────────────────────────
 * EXPORTED UTILITIES (for developer use)
 * ─────────────────────────────────────────────
 */
export {
  computeCraftLineScore,
  computeVerificationScore,
  getVerificationGate,
  CRAFT_LINE_WEIGHTS,
  VERIFICATION_POINTS,
  VERIFICATION_GATES,
  authStub,
  paymentStub,
  realtimeStub,
  uploadStub,
};
